'use client'
import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useLanguage } from "@/contexts/I18nProvider";
import { getLocalizedProjectUrl } from "@/utils/projectUtils";
import { createPortal } from "react-dom";
import { useModules } from "@/contexts/ModuleProvider";
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import { Earth, Map as MapIcon } from "lucide-react";
import projectService from "@/templates/Starglobal3D/utils/services/project";
import { useSector } from "@/contexts/SectorContext";
import { sanitizeHtml } from '@/utils/sanitize-html';

interface MapOverlayProps {
    isOpen: boolean;
    onClose: () => void;
}

import { CloseIcon, FilterIcon } from './MapOverlayComponents/icons';



import { mapTranslations } from './MapOverlayComponents/translations';
import { FilterPopup } from './MapOverlayComponents/FilterPopup';
import CesiumGlobeView, { type CesiumGlobeViewHandle } from './MapOverlayComponents/CesiumGlobeView';


interface FeatureBounds {
    minLng: number;
    maxLng: number;
    minLat: number;
    maxLat: number;
}

interface GeojsonFeatureWithBBox {
    feature: any;
    bbox: FeatureBounds | null;
}

const getProjectCountry = (project: any): string => {
    const lat = Number(project.lat);
    const lng = Number(project.lng);
    if (isNaN(lat) || isNaN(lng)) return 'vietnam';

    // USA: Lat: [24.0, 50.0], Lng: [-125.0, -65.0]
    if (lng >= -125.0 && lng <= -65.0 && lat >= 24.0 && lat <= 50.0) {
        return 'usa';
    }
    // Canada: Lat: [41.0, 84.0], Lng: [-142.0, -52.0]
    if (lng >= -142.0 && lng <= -52.0 && lat >= 41.0 && lat <= 84.0) {
        return 'canada';
    }
    // Australia: Lat: [-44.0, -9.0], Lng: [112.0, 154.0]
    if (lng >= 112.0 && lng <= 154.0 && lat >= -44.0 && lat <= -9.0) {
        return 'australia';
    }
    return 'vietnam';
};

const getProjectDisplayName = (project: any, locale: string): string => {
    if (locale === 'en') {
        return project.display_en || project.title || project.db_name || project.name || 'Untitled project';
    }
    if (locale === 'ja') {
        return project.display_ja || project.display_en || project.title || project.db_name || project.name || 'Untitled project';
    }
    return (
        project.display_vn ||
        project.display_en ||
        project.title ||
        project.db_name ||
        project.name ||
        'Untitled project'
    );
};

const isPointInBBox = (point: [number, number], bbox: FeatureBounds): boolean => {
    const [lng, lat] = point;
    return lng >= bbox.minLng && lng <= bbox.maxLng && lat >= bbox.minLat && lat <= bbox.maxLat;
};

const isPointInPolygon = (point: [number, number], polygon: number[][][]): boolean => {
    const [x, y] = point;
    let inside = false;

    const exterior = polygon[0];
    if (!exterior) return false;

    for (let i = 0, j = exterior.length - 1; i < exterior.length; j = i++) {
        const xi = exterior[i][0], yi = exterior[i][1];
        const xj = exterior[j][0], yj = exterior[j][1];

        const intersect = ((yi > y) !== (yj > y))
            && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
        if (intersect) inside = !inside;
    }

    if (!inside) return false;

    for (let k = 1; k < polygon.length; k++) {
        const hole = polygon[k];
        let inHole = false;
        for (let i = 0, j = hole.length - 1; i < hole.length; j = i++) {
            const xi = hole[i][0], yi = hole[i][1];
            const xj = hole[j][0], yj = hole[j][1];

            const intersect = ((yi > y) !== (yj > y))
                && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
            if (intersect) inHole = !inHole;
        }
        if (inHole) return false;
    }

    return true;
};

const isPointInMultiPolygon = (point: [number, number], multipolygon: number[][][][]): boolean => {
    for (const polygon of multipolygon) {
        if (isPointInPolygon(point, polygon)) {
            return true;
        }
    }
    return false;
};

const isPointInFeature = (point: [number, number], geometry: any): boolean => {
    if (!geometry) return false;
    if (geometry.type === 'Polygon') {
        return isPointInPolygon(point, geometry.coordinates);
    } else if (geometry.type === 'MultiPolygon') {
        return isPointInMultiPolygon(point, geometry.coordinates);
    }
    return false;
};

const getFeatureBounds = (feature: any): FeatureBounds | null => {
    if (!feature || !feature.geometry) return null;
    let minLng = 180, maxLng = -180, minLat = 90, maxLat = -90;

    const processCoord = (coord: number[]) => {
        const lng = coord[0];
        const lat = coord[1];
        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
    };

    const processPolygon = (polygon: number[][][]) => {
        for (const ring of polygon) {
            for (const coord of ring) {
                processCoord(coord);
            }
        }
    };

    const geom = feature.geometry;
    if (geom.type === 'Polygon') {
        processPolygon(geom.coordinates);
    } else if (geom.type === 'MultiPolygon') {
        for (const polygon of geom.coordinates) {
            processPolygon(polygon);
        }
    }

    if (minLng > maxLng || minLat > maxLat) return null;
    return { minLng, maxLng, minLat, maxLat };
};

const computeProjectsBounds = (projects: any[]) => {
    let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
    let valid = false;
    projects.forEach(p => {
        const lat = Number(p.lat);
        const lng = Number(p.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
        valid = true;
    });
    return valid ? { minLat, maxLat, minLng, maxLng } : null;
};

const getProvinceOfProjectViaGeojson = (project: any, featuresWithBBoxes: GeojsonFeatureWithBBox[]): string => {
    const lat = Number(project.lat);
    const lng = Number(project.lng);
    if (isNaN(lat) || isNaN(lng)) return 'Khác';

    const point: [number, number] = [lng, lat];
    for (const item of featuresWithBBoxes) {
        if (item.bbox && isPointInBBox(point, item.bbox)) {
            if (isPointInFeature(point, item.feature.geometry)) {
                return item.feature.properties.ten_tinh;
            }
        }
    }
    return 'Khác';
};

interface ListPopupProps {
    locale: string;
    allProjects: any[];
    onSelectionChange?: (projects: any[], isActive: boolean) => void;
    onProjectClick?: (project: any) => void;
    mapReady?: boolean;
    isOpen?: boolean;
    onCloseFilter?: () => void;
    featuresWithBBoxes?: GeojsonFeatureWithBBox[];
    onProvinceChange?: (provinces: string[]) => void;
    provinceMode: '34' | '63';
    selectedCountry: string;
    onCountryChange: (country: string) => void;
    selectedProvinces: string[];
    setSelectedProvinces: React.Dispatch<React.SetStateAction<string[]>>;
    selectedFields: string[];
    setSelectedFields: React.Dispatch<React.SetStateAction<string[]>>;
    selectedProjectUrls: string[];
    setSelectedProjectUrls: React.Dispatch<React.SetStateAction<string[]>>;
}

const northernProvinces = [
    "Tuyên Quang", "Lào Cai", "Thái Nguyên", "Phú Thọ", "Bắc Ninh",
    "Hưng Yên", "Hải Phòng", "Ninh Bình", "Cao Bằng", "Điện Biên",
    "Lai Châu", "Lạng Sơn", "Quảng Ninh", "Sơn La", "Hà Nội",
    "Bắc Giang", "Bắc Kạn", "Hà Giang", "Hà Nam", "Hải Dương",
    "Hòa Bình", "Nam Định", "Thái Bình", "Vĩnh Phúc", "Yên Bái"
];

const centralProvinces = [
    "Quảng Trị", "Đà Nẵng", "Quảng Ngãi", "Gia Lai", "Khánh Hòa",
    "Lâm Đồng", "Đắk Lắk", "Đăk Lăk", "Hà Tĩnh", "Nghệ An", "Thanh Hóa", "Huế", "Thừa Thiên Huế",
    "Bình Định", "Bình Thuận", "Đăk Nông", "Kon Tum", "Ninh Thuận", "Phú Yên", "Quảng Bình", "Quảng Nam"
];

const southernProvinces = [
    "TP. Hồ Chí Minh", "Đồng Nai", "Tây Ninh", "Cần Thơ", "Cần Thơ", "Vĩnh Long",
    "Đồng Tháp", "Cà Mau", "An Giang",
    "Bà Rịa - Vũng Tàu", "Bà Rịa -Vũng Tàu", "Bạc Liêu", "Bến Tre", "Bình Dương", "Bình Phước",
    "Hậu Giang", "Kiên Giang", "Kien Giang", "Long An", "Sóc Trăng", "Tiền Giang", "Trà Vinh"
];

const provinceEquivalences: Record<string, string[]> = {
    "Huế": ["Thừa Thiên Huế"],
    "Thừa Thiên Huế": ["Huế"],
    "Đắk Lắk": ["Đăk Lăk", "Đắc Lắk", "Đắc Lăk"],
    "Đăk Lăk": ["Đắk Lắk", "Đắc Lắk", "Đắc Lăk"],
    "Đắc Lắk": ["Đắk Lắk", "Đăk Lăk", "Đắc Lăk"],
    "Đắc Lăk": ["Đắk Lắk", "Đăk Lăk", "Đắc Lắk"],
    "Cần Thơ": ["Cần Thơn"],
    "Cần Thơn": ["Cần Thơ"],
    "Kiên Giang": ["Kien Giang"],
    "Kien Giang": ["Kiên Giang"],
    "Bà Rịa - Vũng Tàu": ["Bà Rịa -Vũng Tàu", "Bà Rịa Vũng Tàu", "Vũng Tàu"],
    "Bà Rịa -Vũng Tàu": ["Bà Rịa - Vũng Tàu", "Bà Rịa Vũng Tàu", "Vũng Tàu"],
    "Bà Rịa Vũng Tàu": ["Bà Rịa - Vũng Tàu", "Bà Rịa -Vũng Tàu", "Vũng Tàu"],
    "Vũng Tàu": ["Bà Rịa - Vũng Tàu", "Bà Rịa -Vũng Tàu", "Bà Rịa Vũng Tàu"],
    "Đắk Nông": ["Đăk Nông", "Đắc Nông"],
    "Đăk Nông": ["Đắk Nông", "Đắc Nông"],
    "Đắc Nông": ["Đắk Nông", "Đăk Nông"]
};

const getEquivalentProvince = (name: string, availableList: string[]): string | null => {
    if (availableList.includes(name)) return name;

    const lowerName = name.toLowerCase().trim();
    const caseMatch = availableList.find(p => p.toLowerCase().trim() === lowerName);
    if (caseMatch) return caseMatch;

    // Check Japanese/English name from provinceNameMap
    for (const [vnName, names] of Object.entries(provinceNameMap)) {
        if (names.en.toLowerCase() === lowerName || names.ja.toLowerCase() === lowerName) {
            const match = availableList.find(p => p.toLowerCase().trim() === vnName.toLowerCase().trim());
            if (match) return match;
            return vnName;
        }
    }

    const equivs = provinceEquivalences[name];
    if (equivs) {
        for (const eq of equivs) {
            const match = availableList.find(p => p.toLowerCase().trim() === eq.toLowerCase().trim());
            if (match) return match;
        }
    }

    for (const [key, values] of Object.entries(provinceEquivalences)) {
        if (values.some(v => v.toLowerCase().trim() === lowerName)) {
            const matchKey = availableList.find(p => p.toLowerCase().trim() === key.toLowerCase().trim());
            if (matchKey) return matchKey;
            for (const v of values) {
                const matchVal = availableList.find(p => p.toLowerCase().trim() === v.toLowerCase().trim());
                if (matchVal) return matchVal;
            }
        }
    }

    return null;
};

const provinceMergerMapping: Record<string, string> = {
    "Hà Giang": "Tuyên Quang",
    "Yên Bái": "Lào Cai",
    "Bắc Kạn": "Thái Nguyên",
    "Hòa Bình": "Phú Thọ",
    "Vĩnh Phúc": "Phú Thọ",
    "Bắc Giang": "Bắc Ninh",
    "Thái Bình": "Hưng Yên",
    "Hải Dương": "Hải Phòng",
    "Hà Nam": "Ninh Bình",
    "Nam Định": "Ninh Bình",
    "Quảng Bình": "Quảng Trị",
    "Quảng Nam": "Đà Nẵng",
    "Kon Tum": "Quảng Ngãi",
    "Bình Định": "Gia Lai",
    "Ninh Thuận": "Khánh Hòa",
    "Đăk Nông": "Lâm Đồng",
    "Đắc Nông": "Lâm Đồng",
    "Bình Thuận": "Lâm Đồng",
    "Phú Yên": "Đắk Lắk",
    "Bà Rịa - Vũng Tàu": "Đồng Nai",
    "Bà Rịa -Vũng Tàu": "Đồng Nai",
    "Bình Dương": "Đồng Nai",
    "Bình Phước": "Đồng Nai",
    "Bạc Liêu": "Cà Mau",
    "Hậu Giang": "Cần Thơ",
    "Sóc Trăng": "Cần Thơ",
    "Bến Tre": "Vĩnh Long",
    "Trà Vinh": "Vĩnh Long",
    "Long An": "Đồng Tháp",
    "Tiền Giang": "Đồng Tháp",
    "Kiên Giang": "An Giang",
    "Kien Giang": "An Giang"
};

const provinceNameMap: Record<string, { en: string; ja: string }> = {
    "Hà Nội": { en: "Hanoi", ja: "ハノイ" },
    "TP. Hồ Chí Minh": { en: "Ho Chi Minh City", ja: "ホーチミン市" },
    "Hồ Chí Minh": { en: "Ho Chi Minh City", ja: "ホーチミン市" },
    "Đà Nẵng": { en: "Da Nang", ja: "ダナン" },
    "Hải Phòng": { en: "Haiphong", ja: "ハイフォン" },
    "Cần Thơ": { en: "Can Tho", ja: "カントー" },
    "Huế": { en: "Hue", ja: "フエ" },
    "Thừa Thiên Huế": { en: "Thua Thien Hue", ja: "トゥアティエン＝フエ" },
    "Quảng Ninh": { en: "Quang Ninh", ja: "クアンニン" },
    "Hải Dương": { en: "Hai Duong", ja: "ハイズオン" },
    "Bắc Ninh": { en: "Bac Ninh", ja: "バクニン" },
    "Hưng Yên": { en: "Hung Yen", ja: "フンイエン" },
    "Hà Nam": { en: "Ha Nam", ja: "ハナム" },
    "Nam Định": { en: "Nam Dinh", ja: "ナムディン" },
    "Thái Bình": { en: "Thai Binh", ja: "タイビン" },
    "Vĩnh Phúc": { en: "Vinh Phuc", ja: "ヴィンフック" },
    "Bắc Giang": { en: "Bac Giang", ja: "バクザン" },
    "Phú Thọ": { en: "Phu Tho", ja: "フートー" },
    "Thái Nguyên": { en: "Thai Nguyen", ja: "タイグエン" },
    "Bắc Kạn": { en: "Bac Kan", ja: "バックン" },
    "Tuyên Quang": { en: "Tuyen Quang", ja: "トゥエンクアン" },
    "Lào Cai": { en: "Lao Cai", ja: "ラオカイ" },
    "Yên Bái": { en: "Yen Bai", ja: "イエンバイ" },
    "Lai Châu": { en: "Lai Chau", ja: "ライチャウ" },
    "Điện Biên": { en: "Dien Bien", ja: "ディエンビエン" },
    "Sơn La": { en: "Son La", ja: "ソンラ" },
    "Hòa Bình": { en: "Hoa Binh", ja: "ホアビン" },
    "Hà Giang": { en: "Ha Giang", ja: "ハザン" },
    "Cao Bằng": { en: "Cao Bang", ja: "カオバン" },
    "Lạng Sơn": { en: "Lang Son", ja: "ランソン" },
    "Ninh Bình": { en: "Ninh Binh", ja: "ニンビン" },
    "Quảng Trị": { en: "Quang Tri", ja: "クアンチ" },
    "Quảng Ngãi": { en: "Quang Ngai", ja: "クアンガイ" },
    "Quảng Bình": { en: "Quang Binh", ja: "クアンビン" },
    "Quảng Nam": { en: "Quang Nam", ja: "クアンナム" },
    "Thanh Hóa": { en: "Thanh Hoa", ja: "タインホア" },
    "Nghệ An": { en: "Nghe An", ja: "ゲアン" },
    "Hà Tĩnh": { en: "Ha Tinh", ja: "ハティン" },
    "Bình Định": { en: "Binh Dinh", ja: "ビンディン" },
    "Bình Thuận": { en: "Binh Thuan", ja: "ビントゥアン" },
    "Phú Yên": { en: "Phu Yen", ja: "フーイエン" },
    "Khánh Hòa": { en: "Khanh Hoa", ja: "カインホア" },
    "Ninh Thuận": { en: "Ninh Thuan", ja: "ニントゥアン" },
    "Gia Lai": { en: "Gia Lai", ja: "ザライ" },
    "Kon Tum": { en: "Kon Tum", ja: "コントゥム" },
    "Đắk Lắk": { en: "Dak Lak", ja: "ダクラク" },
    "Đăk Lăk": { en: "Dak Lak", ja: "ダクラク" },
    "Đắk Nông": { en: "Dak Nong", ja: "ダクノン" },
    "Đăk Nông": { en: "Dak Nong", ja: "ダクノン" },
    "Lâm Đồng": { en: "Lam Dong", ja: "ラムドン" },
    "Đồng Nai": { en: "Dong Nai", ja: "ドンナイ" },
    "Bà Rịa - Vũng Tàu": { en: "Ba Ria - Vung Tau", ja: "バリア＝ブンタウ" },
    "Bà Rịa -Vũng Tàu": { en: "Ba Ria - Vung Tau", ja: "バリア＝ブンタウ" },
    "Bình Dương": { en: "Binh Duong", ja: "ビンズオン" },
    "Bình Phước": { en: "Binh Phuoc", ja: "ビンフオック" },
    "Tây Ninh": { en: "Tay Ninh", ja: "タイニン" },
    "Long An": { en: "Long An", ja: "ロンアン" },
    "Đồng Tháp": { en: "Dong Thap", ja: "ドンタップ" },
    "Tiền Giang": { en: "Tien Giang", ja: "ティエンザン" },
    "Bến Tre": { en: "Ben Tre", ja: "ベンチェ" },
    "Trà Vinh": { en: "Tra Vinh", ja: "チャーヴィン" },
    "Vĩnh Long": { en: "Vinh Long", ja: "ヴィンロン" },
    "An Giang": { en: "An Giang", ja: "アンザン" },
    "Kiên Giang": { en: "Kien Giang", ja: "キエンザン" },
    "Kien Giang": { en: "Kien Giang", ja: "キエンザン" },
    "Hậu Giang": { en: "Hau Giang", ja: "ハウザン" },
    "Sóc Trăng": { en: "Soc Trang", ja: "ソクチャン" },
    "Bạc Liêu": { en: "Bac Lieu", ja: "バクリエウ" },
    "Cà Mau": { en: "Ca Mau", ja: "カマウ" },
};

const getProvinceName = (prov: string, locale: string): string => {
    const name = provinceNameMap[prov];
    if (!name) return prov;
    if (locale === 'en') return name.en;
    if (locale === 'ja') return name.ja;
    return prov;
};

const getProjectNamePath = (item: any): string => {
    if (!item) return '';
    if (item.name_path && item.name_path.trim() !== "") {
        return item.name_path.replace(/\/+$/, '').replace(/^\/+/, '');
    }
    if (item.url && item.url.startsWith('http')) {
        try {
            const parsedUrl = new URL(item.url);
            return parsedUrl.pathname.replace(/\/+$/, '').replace(/^\/+/, '');
        } catch (e) {
            // fall through
        }
    }
    return (item.url || item.title || '').replace(/\/+$/, '');
};

const ListPopup = ({
    locale,
    allProjects,
    onSelectionChange,
    onProjectClick,
    mapReady = true,
    isOpen = false,
    onCloseFilter,
    featuresWithBBoxes = [],
    onProvinceChange,
    provinceMode,
    selectedCountry,
    onCountryChange,
    selectedProvinces,
    setSelectedProvinces,
    selectedFields,
    setSelectedFields,
    selectedProjectUrls,
    setSelectedProjectUrls
}: ListPopupProps) => {
    const t = mapTranslations[locale as 'en' | 'vn' | 'ja'] || mapTranslations.vn;

    const [provinceSearch, setProvinceSearch] = useState<string>('');
    const [isAllSelectedOpen, setIsAllSelectedOpen] = useState(false);
    const [isProvinceDropdownOpen, setIsProvinceDropdownOpen] = useState(false);
    const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
    const [pendingAiFilter, setPendingAiFilter] = useState<{ region?: string, fields?: string[], provinces?: string[], country?: string } | null>(null);
    const invalidProvincesInfoRef = useRef<any[]>([]);
    // Selections are cleaned up inside the explicit handleSwitchProvinceMode event listener to avoid mount-time conflicts.

    useEffect(() => {
        const handleFocusMapProject = (e: any) => {
            const projectUrl = e.detail?.project_url;
            if (!projectUrl || allProjects.length === 0) return;

            const cleanUrl = projectUrl.replace(/\/+$/, '').replace(/^\/+/, '');
            const targetItem = allProjects.find(p => {
                const pKey = getProjectNamePath(p);
                return pKey === cleanUrl;
            });
            if (targetItem) {
                const targetKey = getProjectNamePath(targetItem);
                if (targetKey) {
                    setSelectedProjectUrls([targetKey]);
                    setSelectedProvinces([]);
                    setSelectedFields([]);
                }
            }
        };

        window.addEventListener('focus_map_project', handleFocusMapProject);
        return () => window.removeEventListener('focus_map_project', handleFocusMapProject);
    }, [allProjects]);

    useEffect(() => {
        setIsProvinceDropdownOpen(false);
        setIsCountryDropdownOpen(false);
        setProvinceSearch('');
        setExpandedCategory('');
    }, [isOpen]);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const [validFields, setValidFields] = useState<string[]>([]);
    const [sectorsData, setSectorsData] = useState<any[]>([]);
    const [loadingFields, setLoadingFields] = useState(true);

    const [expandedCategory, setExpandedCategory] = useState<string>('');
    const [projectMap, setProjectMap] = useState<Record<string, { title: string, url?: string, name_path?: string, lat?: number, lng?: number, display_vn?: string, display_en?: string, display_ja?: string, thumbnail?: string, map?: any }[]>>({});
    const [loadingMap, setLoadingMap] = useState<Record<string, boolean>>({});
    const [selectedProject, setSelectedProject] = useState<string | null>(null);

    const { sectors: sectorsFromContext } = useSector();

    useEffect(() => {
        const fetchFields = async () => {
            try {
                const fieldsRes = await projectService.getAllFields();

                if (sectorsFromContext.length > 0 && fieldsRes?.data) {
                    setSectorsData(sectorsFromContext);

                    const rawFields = fieldsRes.data.map((f: any) => f.field);
                    const validSpottedFields = rawFields
                        .flatMap((field: string) => field ? field.split(',').map((s: string) => s.trim()) : [])
                        .filter(Boolean);

                    const filteredSectors = sectorsFromContext.filter((sector: any) => {
                        return validSpottedFields.includes(sector.id_field?.trim()) ||
                            validSpottedFields.includes(sector.parent?.trim());
                    });

                    const sortedSectors = [...filteredSectors].sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0));
                    const sortedFieldIds = sortedSectors.map((s: any) => s.id_field?.trim()).filter(Boolean);

                    const uniqueFields = Array.from(new Set<string>(sortedFieldIds));
                    setValidFields(uniqueFields);
                } else if (fieldsRes?.data) {
                    const rawFields = fieldsRes.data.map((f: any) => f.field);
                    const validSpottedFields = rawFields
                        .flatMap((field: string) => field ? field.split(',').map((s: string) => s.trim()) : [])
                        .filter(Boolean);
                    const uniqueFields = Array.from(new Set<string>(validSpottedFields));
                    setValidFields(uniqueFields);
                }
            } catch (err) {
                console.error('Failed to fetch available fields for Map List:', err);
                setValidFields(Object.keys(t.fields));
            } finally {
                setLoadingFields(false);
            }
        };
        fetchFields();
    }, [t.fields, sectorsFromContext]);

    useEffect(() => {
        if (!expandedCategory || projectMap[expandedCategory] || loadingMap[expandedCategory]) return;

        const fetchProjectsForCategory = async () => {
            setLoadingMap(prev => ({ ...prev, [expandedCategory]: true }));
            try {
                const { data } = await projectService.getProjectsByLocation({
                    limit: 100,
                    active: 1,
                    field: expandedCategory
                });

                const mappedData = data && Array.isArray(data) ? data.map((proj: any) => ({
                    title: proj.title || proj.db_name,
                    display_vn: proj.display_vn,
                    display_en: proj.display_en,
                    display_ja: proj.display_ja,
                    url: proj.url,
                    name_path: proj.name_path,
                    lat: proj.lat,
                    lng: proj.lng,
                    map: proj.map
                })) : [];

                setProjectMap(prev => ({ ...prev, [expandedCategory]: mappedData }));
            } catch (err) {
                console.error(`Failed to fetch projects for map list (${expandedCategory}):`, err);
                setProjectMap(prev => ({ ...prev, [expandedCategory]: [] }));
            } finally {
                setLoadingMap(prev => ({ ...prev, [expandedCategory]: false }));
            }
        };

        fetchProjectsForCategory();
    }, [expandedCategory, locale, projectMap, loadingMap]);

    useEffect(() => {
        const handleClickOutsideDropdown = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsProvinceDropdownOpen(false);
                setIsCountryDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutsideDropdown);
        return () => document.removeEventListener('mousedown', handleClickOutsideDropdown);
    }, []);

    const provincesList = useMemo(() => {
        if (!featuresWithBBoxes || featuresWithBBoxes.length === 0) return [];
        const provinces = featuresWithBBoxes.map(f => f.feature.properties.ten_tinh);
        const uniqueProvinces = Array.from(new Set(provinces)).filter(Boolean);
        uniqueProvinces.sort((a, b) => a.localeCompare(b, 'vi'));
        return uniqueProvinces;
    }, [featuresWithBBoxes]);

    useEffect(() => {
        if (onProvinceChange) {
            onProvinceChange(selectedProvinces);
        }
    }, [selectedProvinces, onProvinceChange]);

    // Lắng nghe sự kiện lọc từ AI Agent
    useEffect(() => {
        const handleAiFilter = (e: any) => {
            const { region, fields, provinces, country } = e.detail || {};
            setPendingAiFilter({ region, fields, provinces, country });
        };

        window.addEventListener('filter_map_projects', handleAiFilter);

        // Xử lý sự kiện bị bỏ lỡ nếu component mount sau khi sự kiện kích hoạt
        if (typeof window !== 'undefined' && (window as any).__pendingAiFilterData) {
            handleAiFilter({ detail: (window as any).__pendingAiFilterData });
        }

        return () => {
            window.removeEventListener('filter_map_projects', handleAiFilter);
        };
    }, []);

    // Áp dụng bộ lọc từ AI vào state của checkbox tỉnh thành và dự án
    useEffect(() => {
        if (pendingAiFilter && pendingAiFilter.country) {
            const cLower = pendingAiFilter.country.toLowerCase().trim();
            let targetCountry = '';
            if (['việt nam', 'viet nam', 'vietnam', 'ベトナム'].includes(cLower)) targetCountry = 'vietnam';
            else if (['mỹ', 'my', 'usa', 'united states', 'アメリカ'].includes(cLower)) targetCountry = 'usa';
            else if (['canada', 'カナダ'].includes(cLower)) targetCountry = 'canada';
            else if (['úc', 'uc', 'australia', 'オーストラリア'].includes(cLower)) targetCountry = 'australia';
            else if (['nước ngoài', 'abroad', '海外'].includes(cLower)) targetCountry = 'abroad';

            if (targetCountry && selectedCountry !== targetCountry) {
                onCountryChange(targetCountry);
                return;
            }
        }

        if (pendingAiFilter && allProjects.length > 0 && provincesList.length > 0) {
            const { region, fields, provinces, country } = pendingAiFilter;

            let targetProvinces: string[] = [];
            let invalidProvincesInfo: any[] = [];

            // 1. Lọc theo tỉnh thành
            if (Array.isArray(provinces) && provinces.length > 0) {
                const validProvinces: string[] = [];
                const invalidProvinces: string[] = [];

                provinces.forEach(p => {
                    const equivalent = getEquivalentProvince(p, provincesList);
                    if (equivalent) {
                        validProvinces.push(equivalent);
                    } else {
                        invalidProvinces.push(p);
                    }
                });

                targetProvinces = validProvinces;

                const invalidProvincesToReport = invalidProvinces.filter(p => !provincesList.includes(p));
                if (invalidProvincesToReport.length > 0 && provinceMode === '34') {
                    invalidProvincesInfo = invalidProvincesToReport.map(p => {
                        const parent = provinceMergerMapping[p];
                        return {
                            name: p,
                            parentProvince: parent || null,
                            message: parent
                                ? `Tỉnh ${p} không nằm trong danh sách 34 tỉnh do đã được sáp nhập vào tỉnh ${parent}.`
                                : `Tỉnh ${p} không nằm trong danh sách 34 tỉnh.`
                        };
                    });
                }
            }
            // 2. Lọc theo vùng miền
            else if (region) {
                const r = region.toLowerCase().trim();
                if (r === 'miền bắc' || r === 'northern' || r === '北部') {
                    targetProvinces = northernProvinces.filter(p => provincesList.includes(p));
                } else if (r === 'miền trung' || r === 'central' || r === '中部') {
                    targetProvinces = centralProvinces.filter(p => provincesList.includes(p));
                } else if (r === 'miền nam' || r === 'southern' || r === '南部') {
                    targetProvinces = southernProvinces.filter(p => provincesList.includes(p));
                }
            }

            invalidProvincesInfoRef.current = invalidProvincesInfo;
            setSelectedProvinces(targetProvinces);

            const isVietnamFilter = country && (country.toLowerCase() === 'việt nam' || country.toLowerCase() === 'viet nam');

            // 3. Lọc theo lĩnh vực (chuyển đổi thành việc tích chọn các checkbox dự án)
            if (Array.isArray(fields) && fields.length > 0) {
                setSelectedFields(fields);
                const projectsToSelect = allProjects.filter(p => {
                    if (!p.field) return false;
                    const fieldsOfProj = p.field.split(',').map((s: string) => s.trim());
                    const matchField = fields.some((f: string) => fieldsOfProj.includes(f));

                    const hasLocationFilter = (Array.isArray(provinces) && provinces.length > 0) || !!region;

                    if (hasLocationFilter) {
                        if (targetProvinces.length === 0) return false;
                        const projectProv = getProvinceOfProjectViaGeojson(p, featuresWithBBoxes);
                        return matchField && targetProvinces.includes(projectProv);
                    }

                    if (isVietnamFilter) {
                        const projectProv = getProvinceOfProjectViaGeojson(p, featuresWithBBoxes);
                        return matchField && projectProv !== 'Khác';
                    }

                    return matchField;
                });

                const urls = projectsToSelect.map(p => getProjectNamePath(p)).filter(Boolean) as string[];
                setSelectedProjectUrls(urls);
            } else if (isVietnamFilter) {
                // Chỉ lọc theo Việt Nam, không lọc theo lĩnh vực
                setSelectedFields([]);
                const projectsToSelect = allProjects.filter(p => {
                    const projectProv = getProvinceOfProjectViaGeojson(p, featuresWithBBoxes);
                    return projectProv !== 'Khác';
                });
                const urls = projectsToSelect.map(p => getProjectNamePath(p)).filter(Boolean) as string[];
                setSelectedProjectUrls(urls);
            } else {
                setSelectedFields([]);
                setSelectedProjectUrls([]);
            }

            setPendingAiFilter(null);
        }
    }, [pendingAiFilter, allProjects, provincesList, featuresWithBBoxes, provinceMode, selectedCountry, onCountryChange]);

    useEffect(() => {
        if (!allProjects || allProjects.length === 0) return;

        let filtered = allProjects;
        if (selectedProvinces.length > 0) {
            filtered = filtered.filter(p => selectedProvinces.includes(getProvinceOfProjectViaGeojson(p, featuresWithBBoxes)));
        }

        const isFieldFilterActive = selectedFields.length > 0;
        if (isFieldFilterActive) {
            filtered = filtered.filter(p => {
                if (!p.field) return false;
                const fieldsOfProj = p.field.split(',').map((s: string) => s.trim());
                return selectedFields.some(f => fieldsOfProj.includes(f));
            });
        }

        if (selectedProjectUrls.length > 0) {
            filtered = filtered.filter(p => selectedProjectUrls.includes(getProjectNamePath(p)));
        } else if (isFieldFilterActive) {
            filtered = [];
        }

        const isActive = selectedProvinces.length > 0 || selectedProjectUrls.length > 0 || isFieldFilterActive;

        if (onSelectionChange) {
            onSelectionChange(filtered, isActive);
        }

        // Trả kết quả về cho AI Agent thuyết minh
        if (typeof window !== 'undefined') {
            const pendingFilterData = (window as any).__pendingAiFilterData;
            const pendingRequestId = pendingFilterData?.requestId;
            if (pendingRequestId) {
                window.dispatchEvent(new CustomEvent('filter_map_projects_result', {
                    detail: {
                        requestId: pendingRequestId,
                        total: filtered.length,
                        isActive,
                        invalidProvinces: invalidProvincesInfoRef.current,
                        projects: filtered.slice(0, 20).map((project: any) => ({
                            title: getProjectDisplayName(project, locale),
                            display_vn: project.display_vn,
                            display_en: project.display_en,
                            display_ja: project.display_ja,
                            field: project.field,
                            url: project.url,
                            lat: project.lat,
                            lng: project.lng,
                            province: getProvinceOfProjectViaGeojson(project, featuresWithBBoxes)
                        }))
                    }
                }));
                (window as any).__pendingAiFilterData = null;
            }
        }
    }, [selectedProvinces, selectedProjectUrls, selectedFields, allProjects, featuresWithBBoxes, locale]);

    const handleToggleProvince = (prov: string) => {
        setSelectedProvinces(prev =>
            prev.includes(prov)
                ? prev.filter(p => p !== prov)
                : [...prev, prov]
        );
    };

    const handleRemoveProvince = (prov: string) => {
        setSelectedProvinces(prev => prev.filter(p => p !== prov));
    };

    const northExist = useMemo(() => northernProvinces.filter(p => provincesList.includes(p)), [provincesList]);
    const centralExist = useMemo(() => centralProvinces.filter(p => provincesList.includes(p)), [provincesList]);
    const southExist = useMemo(() => southernProvinces.filter(p => provincesList.includes(p)), [provincesList]);

    const toggleRegion = (regionProvinces: string[]) => {
        const exist = regionProvinces.filter(p => provincesList.includes(p));
        if (exist.length === 0) return;

        const isAllChecked = exist.every(p => selectedProvinces.includes(p));

        if (isAllChecked) {
            setSelectedProvinces(prev => prev.filter(p => !exist.includes(p)));
        } else {
            setSelectedProvinces(prev => {
                const next = [...prev];
                exist.forEach(p => {
                    if (!next.includes(p)) next.push(p);
                });
                return next;
            });
        }
    };

    const toggleField = (fieldId: string) => {
        const projects = allProjects.filter(p => {
            const country = getProjectCountry(p);
            if (selectedCountry === 'abroad') {
                if (country === 'vietnam') return false;
            } else if (selectedCountry !== 'all') {
                if (country !== selectedCountry) return false;
            }
            if (country === 'vietnam') {
                const isMapMatch = (p.map === null || p.map === undefined || String(p.map).trim() === "" || String(p.map).trim() === "null")
                    || String(p.map).trim() === provinceMode;
                if (!isMapMatch) return false;
            }
            if (!p.field) return false;
            const fieldsOfProj = p.field.split(',').map((s: string) => s.trim());
            if (!fieldsOfProj.includes(fieldId)) return false;
            if (selectedProvinces.length > 0 && !selectedProvinces.includes(getProvinceOfProjectViaGeojson(p, featuresWithBBoxes))) return false;
            return true;
        });
        const urls = projects.map(p => getProjectNamePath(p)).filter(Boolean) as string[];
        if (urls.length === 0) return;

        const isFullyChecked = urls.every(url => selectedProjectUrls.includes(url));

        if (isFullyChecked) {
            setSelectedProjectUrls(prev => prev.filter(url => !urls.includes(url)));
            setSelectedFields(prev => prev.filter(f => f !== fieldId));
        } else {
            setSelectedProjectUrls(prev => {
                const next = [...prev];
                urls.forEach(url => {
                    if (!next.includes(url)) next.push(url);
                });
                return next;
            });
            setSelectedFields(prev => prev.includes(fieldId) ? prev : [...prev, fieldId]);
        }
    };

    const toggleProjectChecked = (item: any) => {
        const key = getProjectNamePath(item);
        if (!key) return;
        setSelectedProjectUrls(prev =>
            prev.includes(key)
                ? prev.filter(url => url !== key)
                : [...prev, key]
        );
    };

    const ensureProjectChecked = (item: any) => {
        const key = getProjectNamePath(item);
        if (!key) return;
        setSelectedProjectUrls(prev =>
            prev.includes(key) ? prev : [...prev, key]
        );
    };

    const isFieldChecked = (fieldId: string) => {
        if (!allProjects) return false;
        const projects = allProjects.filter(p => {
            const country = getProjectCountry(p);
            if (selectedCountry === 'abroad') {
                if (country === 'vietnam') return false;
            } else if (selectedCountry !== 'all') {
                if (country !== selectedCountry) return false;
            }
            if (country === 'vietnam') {
                const isMapMatch = (p.map === null || p.map === undefined || String(p.map).trim() === "" || String(p.map).trim() === "null")
                    || String(p.map).trim() === provinceMode;
                if (!isMapMatch) return false;
            }
            if (!p.field) return false;
            const fieldsOfProj = p.field.split(',').map((s: string) => s.trim());
            if (!fieldsOfProj.includes(fieldId)) return false;
            if (selectedProvinces.length > 0 && !selectedProvinces.includes(getProvinceOfProjectViaGeojson(p, featuresWithBBoxes))) return false;
            return true;
        });
        const urls = projects.map(p => getProjectNamePath(p)).filter(Boolean) as string[];
        if (urls.length === 0) return false;
        return urls.every(url => selectedProjectUrls.includes(url));
    };

    const handleReset = useCallback(() => {
        setSelectedProvinces([]);
        setSelectedProjectUrls([]);
        setSelectedFields([]);
        setSelectedProject(null);
        if (onSelectionChange) onSelectionChange([], false);
    }, [onSelectionChange]);

    useEffect(() => {
        const handleResetFilterEvent = () => {
            handleReset();
        };
        window.addEventListener('reset_map_filter', handleResetFilterEvent);
        return () => {
            window.removeEventListener('reset_map_filter', handleResetFilterEvent);
        };
    }, [handleReset]);

    const isFieldActiveInProvince = (fieldId: string) => {
        const projectsInCountry = allProjects.filter(p => {
            const country = getProjectCountry(p);
            if (selectedCountry === 'abroad') {
                if (country === 'vietnam') return false;
            } else if (selectedCountry !== 'all') {
                if (country !== selectedCountry) return false;
            }
            if (country === 'vietnam') {
                const isMapMatch = (p.map === null || p.map === undefined || String(p.map).trim() === "" || String(p.map).trim() === "null")
                    || String(p.map).trim() === provinceMode;
                if (!isMapMatch) return false;
            }
            return true;
        });

        if (selectedProvinces.length === 0) {
            return projectsInCountry.some(p => {
                if (!p.field) return false;
                return p.field.split(',').map((s: string) => s.trim()).includes(fieldId);
            });
        }
        
        return projectsInCountry.some(p => {
            const province = getProvinceOfProjectViaGeojson(p, featuresWithBBoxes);
            if (!selectedProvinces.includes(province)) return false;
            if (!p.field) return false;
            return p.field.split(',').map((s: string) => s.trim()).includes(fieldId);
        });
    };

    const visibleFields = validFields.filter(fieldId => isFieldActiveInProvince(fieldId));
    const getSectorDisplayName = (fieldId: string) => {
        const sector = sectorsData.find((item: any) => item.id_field?.trim() === fieldId);
        if (locale === 'en') {
            return sector?.display_en?.trim() || fieldId;
        }
        if (locale === 'ja') {
            return sector?.display_ja?.trim() || sector?.display_en?.trim() || sector?.display_vn?.trim() || fieldId;
        }
        return sector?.display_vn?.trim() || sector?.display_en?.trim() || fieldId;
    };

    return (
        <div className="bg-[#1a21338c] backdrop-blur-[6px] rounded-[20px] p-6 text-white w-[90vw] md:w-[400px] shadow-2xl border border-white/10 flex flex-col h-[calc(100vh-220px)] md:h-[calc(100vh-160px)] relative" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>

            {/* Close button for mobile */}
            {onCloseFilter && (
                <button
                    onClick={onCloseFilter}
                    className="md:hidden absolute top-4 right-4 text-white/60 hover:text-white transition-colors p-1 z-50"
                    aria-label="Close filter"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            )}

            {/* LỌC THEO QUỐC GIA & TỈNH THÀNH Section */}
            <div ref={dropdownRef} className="mb-4 shrink-0 relative flex flex-col gap-4">
                
                {/* LỌC THEO QUỐC GIA */}
                <div>
                    <h3 className="text-left text-[12px] font-bold mb-2 uppercase tracking-wider text-white/70">
                        {t.filterCountry}
                    </h3>
                    <div className="relative">
                        <button
                            onClick={() => setIsCountryDropdownOpen(!isCountryDropdownOpen)}
                            className={`w-full text-left px-4 py-3 text-sm text-white flex items-center justify-between transition-all duration-200 border border-white/10 ${isCountryDropdownOpen
                                ? "bg-[#1a2133d9] rounded-t-lg border-b-0"
                                : "bg-white/5 hover:bg-white/10 rounded-lg"
                                }`}
                        >
                            <span className="text-white font-medium">
                                {t.countryNames[selectedCountry as keyof typeof t.countryNames] || selectedCountry}
                            </span>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={2.5}
                                stroke="currentColor"
                                className={`w-4 h-4 text-white/60 transition-transform duration-200 ${isCountryDropdownOpen ? 'rotate-180' : ''}`}
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                            </svg>
                        </button>

                        {isCountryDropdownOpen && (
                            <div className="absolute top-full left-0 w-full bg-[#1a2133e6] backdrop-blur-[12px] rounded-b-lg border border-white/10 border-t-0 shadow-2xl z-50 overflow-hidden flex flex-col">
                                {['all', 'vietnam', 'usa', 'canada', 'australia'].map((c) => (
                                    <button
                                        key={c}
                                        onClick={() => {
                                            onCountryChange(c);
                                            setIsCountryDropdownOpen(false);
                                        }}
                                        className={`w-full text-left px-4 py-3 text-sm transition-colors hover:bg-white/10 ${selectedCountry === c ? 'text-[#FFF500] font-semibold bg-white/5' : 'text-white/80'}`}
                                    >
                                        {t.countryNames[c as keyof typeof t.countryNames] || c}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* LỌC THEO TỈNH THÀNH */}
                <div>
                    <h3 className="text-left text-[12px] font-bold mb-2 uppercase tracking-wider text-white/70">
                        {t.filterProvince}
                    </h3>

                    <div className="relative">
                        <button
                            onClick={() => setIsProvinceDropdownOpen(!isProvinceDropdownOpen)}
                            className={`w-full text-left px-4 py-3 text-sm text-white flex items-center justify-between transition-all duration-200 border border-white/10 ${isProvinceDropdownOpen
                                ? "bg-[#1a2133d9] rounded-t-lg border-b-0"
                                : "bg-white/5 hover:bg-white/10 rounded-lg"
                                }`}
                        >
                            <span className={selectedProvinces.length > 0 ? "text-white font-medium" : "text-white/60"}>
                                {selectedProvinces.length > 0
                                    ? (locale === 'en' ? `Selected ${selectedProvinces.length} provinces` : locale === 'ja' ? `${selectedProvinces.length} 省・市を選択中` : `Đã chọn ${selectedProvinces.length} tỉnh thành`)
                                    : t.selectProvince
                                }
                            </span>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={2.5}
                                stroke="currentColor"
                                className={`w-4 h-4 text-white/60 transition-transform duration-200 ${isProvinceDropdownOpen ? 'rotate-180' : ''}`}
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                            </svg>
                        </button>

                        {isProvinceDropdownOpen && (
                            <div className="absolute top-full left-0 w-full bg-[#1a2133d9] backdrop-blur-[12px] rounded-b-lg border border-white/10 border-t-0 shadow-2xl z-50 max-h-[220px] flex flex-col overflow-hidden">
                                {/* Search bar inside dropdown */}
                                <div className="p-2 border-b border-white/5 sticky top-0 bg-[#1a2133fa] backdrop-blur-[32px] z-10">
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={provinceSearch}
                                            onChange={(e) => setProvinceSearch(e.target.value)}
                                            placeholder={locale === 'ja' ? '省・市を検索...' : locale === 'en' ? "Search province..." : "Tìm tỉnh thành..."}
                                            className="w-full bg-white/5 border border-white/10 rounded-lg pl-8 pr-3 py-1.5 text-xs text-white placeholder-white/40 focus:outline-none focus:border-[#FFF500]/50 transition-colors"
                                        />
                                        <svg className="w-3.5 h-3.5 text-white/40 absolute left-2.5 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                        {provinceSearch && (
                                            <button
                                                onClick={() => setProvinceSearch('')}
                                                className="text-white/40 hover:text-white absolute right-2 top-1/2 -translate-y-1/2 p-1"
                                            >
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Scrollable list */}
                                <div className="overflow-y-auto flex-1 [&::-webkit-scrollbar]:w-[4px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-white/20 [&::-webkit-scrollbar-thumb]:rounded-[4px]">
                                    {!provinceSearch && (
                                        <>
                                            {/* Northern Region */}
                                            {northExist.length > 0 && (
                                                <button
                                                    onClick={() => toggleRegion(northernProvinces)}
                                                    className="w-full text-left px-4 py-2.5 hover:bg-white/5 text-xs text-white flex items-center gap-3 transition-colors border-b border-white/5"
                                                >
                                                    {northExist.every(p => selectedProvinces.includes(p)) ? (
                                                        <div className="w-[14px] h-[14px] rounded-[3px] flex items-center justify-center bg-[#FFF500] shrink-0">
                                                            <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M10 3L4.5 8.5L2 6" stroke="black" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                                            </svg>
                                                        </div>
                                                    ) : (
                                                        <div className="w-[14px] h-[14px] rounded-[3px] border border-white/30 shrink-0 hover:border-[#FFF500]/50 transition-colors bg-white/5" />
                                                    )}
                                                    <span className={northExist.every(p => selectedProvinces.includes(p)) ? 'text-[#FFF500] font-semibold' : 'text-white/80 hover:text-white'}>
                                                        {(locale === 'ja') ? '北部地方' : (locale === 'en' ? 'Northern Provinces' : 'Các tỉnh miền Bắc')}
                                                    </span>
                                                </button>
                                            )}

                                            {/* Central Region */}
                                            {centralExist.length > 0 && (
                                                <button
                                                    onClick={() => toggleRegion(centralProvinces)}
                                                    className="w-full text-left px-4 py-2.5 hover:bg-white/5 text-xs text-white flex items-center gap-3 transition-colors border-b border-white/5"
                                                >
                                                    {centralExist.every(p => selectedProvinces.includes(p)) ? (
                                                        <div className="w-[14px] h-[14px] rounded-[3px] flex items-center justify-center bg-[#FFF500] shrink-0">
                                                            <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M10 3L4.5 8.5L2 6" stroke="black" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                                            </svg>
                                                        </div>
                                                    ) : (
                                                        <div className="w-[14px] h-[14px] rounded-[3px] border border-white/30 shrink-0 hover:border-[#FFF500]/50 transition-colors bg-white/5" />
                                                    )}
                                                    <span className={centralExist.every(p => selectedProvinces.includes(p)) ? 'text-[#FFF500] font-semibold' : 'text-white/80 hover:text-white'}>
                                                        {(locale === 'ja') ? '中部地方' : (locale === 'en' ? 'Central Provinces' : 'Các tỉnh miền Trung')}
                                                    </span>
                                                </button>
                                            )}

                                            {/* Southern Region */}
                                            {southExist.length > 0 && (
                                                <button
                                                    onClick={() => toggleRegion(southernProvinces)}
                                                    className="w-full text-left px-4 py-2.5 hover:bg-white/5 text-xs text-white flex items-center gap-3 transition-colors border-b border-white/5"
                                                >
                                                    {southExist.every(p => selectedProvinces.includes(p)) ? (
                                                        <div className="w-[14px] h-[14px] rounded-[3px] flex items-center justify-center bg-[#FFF500] shrink-0">
                                                            <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M10 3L4.5 8.5L2 6" stroke="black" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                                            </svg>
                                                        </div>
                                                    ) : (
                                                        <div className="w-[14px] h-[14px] rounded-[3px] border border-white/30 shrink-0 hover:border-[#FFF500]/50 transition-colors bg-white/5" />
                                                    )}
                                                    <span className={southExist.every(p => selectedProvinces.includes(p)) ? 'text-[#FFF500] font-semibold' : 'text-white/80 hover:text-white'}>
                                                        {(locale === 'ja') ? '南部地方' : (locale === 'en' ? 'Southern Provinces' : 'Các tỉnh miền Nam')}
                                                    </span>
                                                </button>
                                            )}
                                        </>
                                    )}

                                    {provincesList
                                        .filter(prov => {
                                            const search = provinceSearch.toLowerCase();
                                            const mapped = provinceNameMap[prov];
                                            return prov.toLowerCase().includes(search)
                                                || (mapped?.en.toLowerCase().includes(search))
                                                || (mapped?.ja.toLowerCase().includes(search));
                                        })
                                        .map(prov => {
                                            const isChecked = selectedProvinces.includes(prov);
                                            return (
                                                <button
                                                    key={prov}
                                                    onClick={() => handleToggleProvince(prov)}
                                                    className="w-full text-left px-4 py-2.5 hover:bg-white/5 text-xs text-white flex items-center gap-3 transition-colors"
                                                >
                                                    {isChecked ? (
                                                        <div className="w-[14px] h-[14px] rounded-[3px] flex items-center justify-center bg-[#FFF500] shrink-0">
                                                            <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M10 3L4.5 8.5L2 6" stroke="black" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                                            </svg>
                                                        </div>
                                                    ) : (
                                                        <div className="w-[14px] h-[14px] rounded-[3px] border border-white/30 shrink-0 hover:border-[#FFF500]/50 transition-colors bg-white/5" />
                                                    )}
                                                    <span className={isChecked ? 'text-[#FFF500] font-semibold' : 'text-white/80 hover:text-white'}>
                                                        {getProvinceName(prov, locale)}
                                                    </span>
                                                </button>
                                            );
                                        })
                                    }
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Selected tags area - placed below dropdown selection bar */}
                {selectedProvinces.length > 0 && (
                    <div className="flex flex-col gap-1 mt-2">
                        <div className={`flex flex-wrap gap-1 ${isAllSelectedOpen ? 'max-h-[150px] overflow-y-auto pr-1 [&::-webkit-scrollbar]:w-[3.5px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-white/20 [&::-webkit-scrollbar-thumb]:rounded-[3px]' : 'max-h-[50px] overflow-hidden'}`}>
                            {selectedProvinces.map((prov) => (
                                <div key={prov} className="bg-[#FFF5004D] hover:bg-[#FFF50066] text-[11px] text-white px-2.5 py-0.5 rounded-full flex items-center gap-1 border border-[#B3AC00] transition-all">
                                    <span>{getProvinceName(prov, locale)}</span>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveProvince(prov);
                                        }}
                                        className="text-white/40 hover:text-red-400 transition-colors"
                                    >
                                        <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            ))}
                        </div>
                        {selectedProvinces.length > 3 && (
                            <button
                                onClick={() => setIsAllSelectedOpen(!isAllSelectedOpen)}
                                className="text-[#FFF500] hover:underline text-[10.5px] font-semibold self-start mt-0.5"
                            >
                                {isAllSelectedOpen
                                    ? (locale === 'en' ? 'Show less' : locale === 'ja' ? '折りたたむ' : 'Thu gọn')
                                    : (locale === 'en' ? `View all selected (${selectedProvinces.length})` : locale === 'ja' ? `すべて表示 (${selectedProvinces.length})` : `Xem tất cả đã chọn (${selectedProvinces.length})`)
                                }
                            </button>
                        )}
                    </div>
                )}
            </div>

            <div className="h-px bg-white/10 w-full mb-4 shrink-0" />

            {/* LỌC THEO LĨNH VỰC Section */}
            <h3 className="text-left text-[12px] font-bold mb-3 uppercase tracking-wider text-white/70 shrink-0">
                {t.filterFieldSection}
            </h3>

            {/* Accordion Categories List */}
            <div className="flex-1 overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-[4px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-white/20 [&::-webkit-scrollbar-thumb]:rounded-[4px]">
                {loadingFields ? (
                    <div className="flex justify-center py-8">
                        <div className="w-8 h-8 rounded-full border-4 border-white/20 border-t-yellow-400 animate-spin"></div>
                    </div>
                ) : visibleFields.length === 0 ? (
                    <div className="text-center text-gray-400 py-4 text-sm">Chưa có kết quả</div>
                ) : visibleFields.map((fieldId) => {
                    const isExpanded = expandedCategory === fieldId;
                    const catName = getSectorDisplayName(fieldId).toUpperCase();
                    const isLoadingProj = loadingMap[fieldId];
                    const items = projectMap[fieldId] || [];

                    const filteredItems = items.filter(item => {
                        const country = getProjectCountry(item);
                        if (selectedCountry === 'abroad') {
                            if (country === 'vietnam') return false;
                        } else if (selectedCountry !== 'all') {
                            if (country !== selectedCountry) return false;
                        }
                        if (country === 'vietnam') {
                            const isMapMatch = (item.map === null || item.map === undefined || String(item.map).trim() === "" || String(item.map).trim() === "null")
                                || String(item.map).trim() === provinceMode;
                            if (!isMapMatch) return false;
                        }

                        if (selectedProvinces.length === 0) return true;
                        return selectedProvinces.includes(getProvinceOfProjectViaGeojson(item, featuresWithBBoxes));
                    });

                    return (
                        <div key={fieldId} className={`mb-2 last:mb-0 border transition-all ${isExpanded ? 'bg-white/5 border-white/5' : 'bg-transparent border-transparent'} overflow-hidden`}>
                            <div className="flex items-center px-4 py-3 gap-3">
                                <div
                                    onClick={() => toggleField(fieldId)}
                                    className="cursor-pointer flex items-center justify-center shrink-0"
                                >
                                    {isFieldChecked(fieldId) ? (
                                        <div className="w-[14px] h-[14px] rounded-[3px] flex items-center justify-center bg-[#FFF500] shrink-0">
                                            <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M10 3L4.5 8.5L2 6" stroke="black" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                    ) : (
                                        <div className="w-[14px] h-[14px] rounded-[3px] border border-white/30 shrink-0 hover:border-[#FFF500]/50 transition-colors bg-white/5" />
                                    )}
                                </div>

                                <div
                                    onClick={() => setExpandedCategory(isExpanded ? '' : fieldId)}
                                    className="flex items-center justify-between flex-1 cursor-pointer select-none"
                                >
                                    <span className={`text-[13px] font-semibold transition-colors ${isExpanded ? 'text-[#FFF500]' : 'text-white/95 hover:text-[#FFF500]'}`}>
                                        {catName}
                                    </span>
                                    <div className="flex items-center justify-center w-5 h-5 shrink-0 text-white/40 hover:text-white transition-colors">
                                        {isExpanded ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-3.5 h-3.5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" />
                                            </svg>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-3.5 h-3.5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                                            </svg>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {isExpanded && (
                                <div className="px-4 pb-3 pt-1 border-t border-white/5 bg-black/20 flex flex-col gap-2.5">
                                    {isLoadingProj ? (
                                        <div className="flex items-center gap-2 text-gray-400 text-xs italic py-1">
                                            <div className="w-3.5 h-3.5 rounded-full border-2 border-white/20 border-t-yellow-400 animate-spin" />
                                            Đang tải...
                                        </div>
                                    ) : filteredItems.length > 0 ? filteredItems.map((item, idx) => {
                                        const displayTitle = getProjectDisplayName(item, locale);
                                        const isProjectSelected = selectedProject === `${fieldId}-${idx}`;
                                        const isProjectChecked = selectedProjectUrls.includes(getProjectNamePath(item));
                                        return (
                                            <div
                                                key={idx}
                                                className="flex items-start gap-2 text-[12.5px] transition-colors group text-left w-full py-1.5"
                                            >
                                                <div
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleProjectChecked(item);
                                                    }}
                                                    className="cursor-pointer flex items-center justify-center shrink-0 mt-[3px]"
                                                >
                                                    {isProjectChecked ? (
                                                        <div className="w-[14px] h-[14px] rounded-[3px] flex items-center justify-center bg-[#FFF500] shrink-0">
                                                            <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M10 3L4.5 8.5L2 6" stroke="black" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                                            </svg>
                                                        </div>
                                                    ) : (
                                                        <div className="w-[14px] h-[14px] rounded-[3px] border border-white/30 shrink-0 hover:border-[#FFF500]/50 transition-colors bg-white/5" />
                                                    )}
                                                </div>
                                                <button
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setSelectedProject(`${fieldId}-${idx}`);
                                                        ensureProjectChecked(item);
                                                        if (onProjectClick) {
                                                            onProjectClick(item);
                                                        }
                                                    }}
                                                    className={`flex-1 text-left leading-snug line-clamp-2 transition-colors ${isProjectSelected ? 'text-[#FFF500] font-medium' : 'text-gray-300 hover:text-white'}`}
                                                >
                                                    {displayTitle}
                                                </button>
                                            </div>
                                        );
                                    }) : (
                                        <span className="text-xs text-gray-400 italic py-1">Chưa có dự án nào ở tỉnh thành này.</span>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Đặt lại Button */}
            <div className="mt-4 shrink-0 flex justify-center">
                <button
                    onClick={handleReset}
                    className="border border-white/20 hover:border-white/40 bg-white/5 hover:bg-white/10 text-white text-xs font-semibold py-2.5 px-6 rounded-xl flex items-center gap-2 transition-all shadow-md active:scale-95"
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
                        <path d="M4 4V9H4.58152M19.9381 11C19.446 7.05369 16.0796 4 12 4C8.64262 4 5.76829 6.06817 4.58152 9M4.58152 9H9.5M20 20V15H19.4185M4.06189 13C4.55399 16.9463 7.92038 20 12 20C15.3574 20 18.2317 17.9318 19.4185 15M19.4185 15H14.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {t.reset}
                </button>
            </div>
        </div>
    );
};

const ManeuverIcon = ({ maneuver, size = "size-6" }: { maneuver?: string, size?: string }) => {
    switch (maneuver) {
        case 'turn-left':
        case 'turn-slight-left':
        case 'turn-sharp-left':
            return (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className={size}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                </svg>
            );
        case 'turn-right':
        case 'turn-slight-right':
        case 'turn-sharp-right':
            return (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className={size}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 15l6-6m0 0l-6-6m6 6H9a6 6 0 000 12h3" />
                </svg>
            );
        case 'uturn-left':
        case 'uturn-right':
            return (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className={size}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 15l-6 6m0 0l-6-6m6 6V9a6 6 0 0112 0v3" />
                </svg>
            );
        case 'straight':
            return (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className={size}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 19V5m0 0l-7 7m7-7l7 7" />
                </svg>
            );
        case 'merge':
        case 'fork-left':
        case 'fork-right':
            return (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className={size}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 0 0 6 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0 1 18 16.5h-2.25" />
                </svg>
            );
        default:
            return (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className={size}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 19V5m0 0l-7 7m7-7l7 7" />
                </svg>
            );
    }
};

export default function MapOverlay({ isOpen, onClose }: MapOverlayProps) {
    const { locale } = useLanguage();
    const { activeModules, isFallback } = useModules();
    const isCesiumActive = isFallback ? true : !!activeModules?.['cesiumMap'];
    const t = mapTranslations[locale as 'en' | 'vn' | 'ja'] || mapTranslations.vn;

    const [searchValue, setSearchValue] = useState("");
    const [mapType, setMapType] = useState<'roadmap' | 'satellite' | 'terrain'>('satellite');
    const [mapViewMode, setMapViewMode] = useState<'2d' | 'cesium'>('2d');
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);

    const mapRef = useRef<HTMLDivElement>(null);
    const googleMapInstance = useRef<any>(null);
    const cesiumRef = useRef<CesiumGlobeViewHandle | null>(null);
    const clustererRef = useRef<any>(null);
    const markersRef = useRef<any[]>([]);
    const infoWindowRef = useRef<any>(null);
    const activePopupRef = useRef<string | null>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [selectedCountry, setSelectedCountry] = useState<string>('all');
    const [selectedProvinces, setSelectedProvinces] = useState<string[]>([]);
    const [selectedFields, setSelectedFields] = useState<string[]>([]);
    const [selectedProjectUrls, setSelectedProjectUrls] = useState<string[]>([]);
    const [activeProject, setActiveProject] = useState<string | null>(null);
    const hasInitializedProjectUrlsRef = useRef(false);
    const hasFocusedInitialProjectRef = useRef(false);
    const [isUrlInitialized, setIsUrlInitialized] = useState(false);
    const [provinceMode, setProvinceMode] = useState<'34' | '63'>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('provinceMode');
            if (saved === '34' || saved === '63') return saved as '34' | '63';
        }
        return '34';
    });

    useEffect(() => {
        if (typeof window === 'undefined') return;
        
        if (isOpen) {
            const params = new URLSearchParams(window.location.search);
            const country = params.get('country');
            if (country) setSelectedCountry(country);

            const provinces = params.get('provinces');
            if (provinces) setSelectedProvinces(provinces.split(',').filter(Boolean));

            const fields = params.get('fields');
            if (fields) setSelectedFields(fields.split(',').filter(Boolean));

            const project = params.get('project');
            if (project) setActiveProject(project);

            const projects = params.get('projects');
            if (projects) setSelectedProjectUrls(projects.split(',').filter(Boolean));

            const provMode = params.get('provinceMode') || params.get('mode');
            if (provMode === '34' || provMode === '63') {
                setProvinceMode(provMode);
            }
            
            setIsUrlInitialized(true);
        } else {
            setSelectedCountry('all');
            setSelectedProvinces([]);
            setSelectedFields([]);
            setSelectedProjectUrls([]);
            setActiveProject(null);
            hasInitializedProjectUrlsRef.current = false;
            hasFocusedInitialProjectRef.current = false;
            setIsUrlInitialized(false);
        }
    }, [isOpen]);


    useEffect(() => {
        if (typeof window === 'undefined' || !isOpen || !isUrlInitialized) return;
        
        const url = new URL(window.location.href);
        const isMapUrl = url.pathname.includes('/map3d');
        if (!isMapUrl) return;

        if (selectedCountry && selectedCountry !== 'all') {
            url.searchParams.set('country', selectedCountry);
        } else {
            url.searchParams.delete('country');
        }

        if (selectedProvinces.length > 0) {
            url.searchParams.set('provinces', selectedProvinces.join(','));
        } else {
            url.searchParams.delete('provinces');
        }

        if (selectedFields.length > 0) {
            url.searchParams.set('fields', selectedFields.join(','));
        } else {
            url.searchParams.delete('fields');
        }

        if (selectedProjectUrls.length > 0) {
            url.searchParams.set('projects', selectedProjectUrls.join(','));
        } else {
            url.searchParams.delete('projects');
        }

        if (activeProject) {
            url.searchParams.set('project', activeProject);
        } else {
            url.searchParams.delete('project');
        }

        if (provinceMode) {
            url.searchParams.set('provinceMode', provinceMode);
        } else {
            url.searchParams.delete('provinceMode');
        }

        window.history.replaceState(null, '', url.pathname + url.search);
    }, [selectedCountry, selectedProvinces, selectedFields, selectedProjectUrls, activeProject, provinceMode, isOpen, isUrlInitialized]);

    const [mapReady, setMapReady] = useState(false);
    const [allProjects, setAllProjects] = useState<any[]>([]);

    useEffect(() => {
        if (typeof window !== 'undefined' && isOpen) {
            const saved = localStorage.getItem('provinceMode') as '34' | '63';
            if (saved === '34' || saved === '63') {
                setProvinceMode(saved);
            }
        }
    }, [isOpen]);

    const [rawGeoJson34, setRawGeoJson34] = useState<any>(null);
    const [rawGeoJson63, setRawGeoJson63] = useState<any>(null);
    const [rawGeoJsonUSA, setRawGeoJsonUSA] = useState<any>(null);
    const [rawGeoJsonCAN, setRawGeoJsonCAN] = useState<any>(null);
    const [rawGeoJsonAUS, setRawGeoJsonAUS] = useState<any>(null);

    const filteredProjectsByMap = useMemo(() => {
        return allProjects.filter((p: any) => {
            const country = getProjectCountry(p);
            
            if (selectedCountry === 'all') {
                if (country === 'vietnam') {
                    if (p.map === null || p.map === undefined || String(p.map).trim() === "" || String(p.map).trim() === "null") return true;
                    return String(p.map).trim() === provinceMode;
                }
                return true;
            }
            
            if (selectedCountry === 'abroad') {
                return country !== 'vietnam';
            }
            
            if (country !== selectedCountry) return false;
            
            if (selectedCountry === 'vietnam') {
                if (p.map === null || p.map === undefined || String(p.map).trim() === "" || String(p.map).trim() === "null") return true;
                return String(p.map).trim() === provinceMode;
            }
            return true;
        });
    }, [allProjects, selectedCountry, provinceMode]);

    const featuresWithBBoxes = useMemo<GeojsonFeatureWithBBox[]>(() => {
        let activeGeoJson = null;
        if (selectedCountry === 'vietnam' || selectedCountry === 'all') {
            activeGeoJson = provinceMode === '34' ? rawGeoJson34 : rawGeoJson63;
        } else if (selectedCountry === 'usa') {
            activeGeoJson = rawGeoJsonUSA;
        } else if (selectedCountry === 'canada') {
            activeGeoJson = rawGeoJsonCAN;
        } else if (selectedCountry === 'australia') {
            activeGeoJson = rawGeoJsonAUS;
        } else if (selectedCountry === 'abroad') {
            const features: any[] = [];
            if (rawGeoJsonUSA?.features) features.push(...rawGeoJsonUSA.features);
            if (rawGeoJsonCAN?.features) features.push(...rawGeoJsonCAN.features);
            if (rawGeoJsonAUS?.features) features.push(...rawGeoJsonAUS.features);
            activeGeoJson = { type: 'FeatureCollection', features };
        }

        if (!activeGeoJson || !activeGeoJson.features) return [];
        return activeGeoJson.features.map((f: any) => ({
            feature: f,
            bbox: getFeatureBounds(f)
        }));
    }, [selectedCountry, provinceMode, rawGeoJson34, rawGeoJson63, rawGeoJsonUSA, rawGeoJsonCAN, rawGeoJsonAUS]);

    // Automatically check projects of selected fields on initial load when allProjects is ready
    useEffect(() => {
        if (!allProjects || allProjects.length === 0) return;
        if (selectedFields.length === 0) return;
        if (hasInitializedProjectUrlsRef.current) return;

        if (selectedProjectUrls && selectedProjectUrls.length > 0) {
            hasInitializedProjectUrlsRef.current = true;
            return;
        }

        const urls: string[] = [];
        allProjects.forEach(p => {
            if (!p.field) return;
            const fieldsOfProj = p.field.split(',').map((s: string) => s.trim());
            const matchField = selectedFields.some((f: string) => fieldsOfProj.includes(f));
            
            if (selectedProvinces.length > 0) {
                const projectProv = getProvinceOfProjectViaGeojson(p, featuresWithBBoxes);
                if (matchField && selectedProvinces.includes(projectProv)) {
                    const key = getProjectNamePath(p);
                    if (key) urls.push(key);
                }
            } else {
                if (matchField) {
                    const key = getProjectNamePath(p);
                    if (key) urls.push(key);
                }
            }
        });

        if (urls.length > 0) {
            setSelectedProjectUrls(urls);
            hasInitializedProjectUrlsRef.current = true;
        }
    }, [allProjects, selectedFields, selectedProvinces, featuresWithBBoxes, selectedProjectUrls]);


    const [selectedProjects, setSelectedProjects] = useState<any[]>([]);
    const [isFilterActive, setIsFilterActive] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        if (rawGeoJson34) return;
        const fetchGeoJSON = async () => {
            try {
                const res = await fetch('/geojson/vietnam.geojson');
                const data = await res.json();
                setRawGeoJson34(data);
            } catch (err) {
                console.error("Failed to load vietnam.geojson", err);
            }
        };
        fetchGeoJSON();
    }, [isOpen, rawGeoJson34]);

    useEffect(() => {
        if (!isOpen || provinceMode !== '63') return;
        if (rawGeoJson63) return;
        const fetchGeoJSON = async () => {
            try {
                const res = await fetch('/geojson/vietnamold.geojson');
                const data = await res.json();
                setRawGeoJson63(data);
            } catch (err) {
                console.error("Failed to load vietnamold.geojson", err);
            }
        };
        fetchGeoJSON();
    }, [isOpen, provinceMode, rawGeoJson63]);

    useEffect(() => {
        if (!isOpen || (selectedCountry !== 'usa' && selectedCountry !== 'abroad')) return;
        if (rawGeoJsonUSA) return;
        const fetchGeoJSON = async () => {
            try {
                const res = await fetch('/geojson/geoBoundaries-USA-ADM1_simplified.geojson');
                const data = await res.json();
                setRawGeoJsonUSA(data);
            } catch (err) {
                console.error("Failed to load USA GeoJSON", err);
            }
        };
        fetchGeoJSON();
    }, [isOpen, selectedCountry, rawGeoJsonUSA]);

    useEffect(() => {
        if (!isOpen || (selectedCountry !== 'canada' && selectedCountry !== 'abroad')) return;
        if (rawGeoJsonCAN) return;
        const fetchGeoJSON = async () => {
            try {
                const res = await fetch('/geojson/geoBoundaries-CAN-ADM1_simplified.geojson');
                const data = await res.json();
                setRawGeoJsonCAN(data);
            } catch (err) {
                console.error("Failed to load Canada GeoJSON", err);
            }
        };
        fetchGeoJSON();
    }, [isOpen, selectedCountry, rawGeoJsonCAN]);

    useEffect(() => {
        if (!isOpen || (selectedCountry !== 'australia' && selectedCountry !== 'abroad')) return;
        if (rawGeoJsonAUS) return;
        const fetchGeoJSON = async () => {
            try {
                const res = await fetch('/geojson/geoBoundaries-AUS-ADM1_simplified.geojson');
                const data = await res.json();
                setRawGeoJsonAUS(data);
            } catch (err) {
                console.error("Failed to load Australia GeoJSON", err);
            }
        };
        fetchGeoJSON();
    }, [isOpen, selectedCountry, rawGeoJsonAUS]);
    
    const [searchCoords, setSearchCoords] = useState<any[]>([]);

    const [directionsMode, setDirectionsMode] = useState(false);
    const [directionsResult, setDirectionsResult] = useState<any>(null);
    const [isUserFocused, setIsUserFocused] = useState(false);
    const isUserFocusedRef = useRef(false);

    // Sync ref with state
    useEffect(() => {
        isUserFocusedRef.current = isUserFocused;
    }, [isUserFocused]);

    const [isStepsExpanded, setIsStepsExpanded] = useState(false);
    const [userLocation, setUserLocation] = useState<{ lat: number, lng: number } | null>(null);
    const watchIdRef = useRef<number | null>(null);

    const directionsServiceRef = useRef<any>(null);
    const directionsRendererRef = useRef<any>(null);
    const activeProjectForDirectionsRef = useRef<any>(null);
    const userMarkerRef = useRef<any>(null); // Custom marker for live location

    const desktopControlsRef = useRef<HTMLDivElement>(null);
    const mobileControlsRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const tgt = event.target as Node;
            const clickInsideDesktop = desktopControlsRef.current && desktopControlsRef.current.contains(tgt);
            const clickInsideMobile = mobileControlsRef.current && mobileControlsRef.current.contains(tgt);

            if (!clickInsideDesktop && !clickInsideMobile) {
                setIsSearchOpen(false);
                setIsFilterOpen(false);
            }
        };

        if (isSearchOpen || isFilterOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        const closeMapPopupGlobal = (event: MouseEvent | TouchEvent) => {
            const tgt = event.target as Element;
            if (activePopupRef.current && infoWindowRef.current) {
                const isInsidePopup = tgt.closest?.('.gm-style-iw-c') || tgt.closest?.('.gm-style-iw-d');
                const isInsideMapMarker = tgt.closest?.('img[src*="icon-vn.gif"]');
                const isListButton = tgt.closest?.('button'); // specifically clicking a list button handles its own state

                if (!isInsidePopup && !isInsideMapMarker && !isListButton) {
                    infoWindowRef.current.close();
                    activePopupRef.current = null;
                }
            }
        };

        document.addEventListener('touchstart', closeMapPopupGlobal);
        document.addEventListener('mousedown', closeMapPopupGlobal);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', closeMapPopupGlobal);
            document.removeEventListener('mousedown', closeMapPopupGlobal);
        };
    }, [isSearchOpen, isFilterOpen]);

    const fitVietnamMainland = useCallback((mapInstance: any) => {
        if (!mapInstance || !(window as any).google?.maps?.LatLngBounds) return;
        
        let swLat = 8.4, swLng = 101.8, neLat = 23.4, neLng = 109.8; // Default Vietnam
        
        if (selectedCountry === 'usa') {
            swLat = 24.396308; swLng = -125.0; neLat = 49.384358; neLng = -66.93457;
        } else if (selectedCountry === 'canada') {
            swLat = 41.676555; swLng = -141.00187; neLat = 83.110626; neLng = -52.61862;
        } else if (selectedCountry === 'australia') {
            swLat = -43.634597; swLng = 113.09176; neLat = -10.051369; neLng = 153.6393;
        } else if (selectedCountry === 'abroad') {
            const bbox = computeProjectsBounds(filteredProjectsByMap);
            if (bbox) {
                swLat = bbox.minLat; swLng = bbox.minLng; neLat = bbox.maxLat; neLng = bbox.maxLng;
            } else {
                swLat = -45; swLng = -145; neLat = 85; neLng = 155;
            }
        }

        const bounds = new (window as any).google.maps.LatLngBounds(
            new (window as any).google.maps.LatLng(swLat, swLng),
            new (window as any).google.maps.LatLng(neLat, neLng)
        );
        if (typeof window !== 'undefined' && window.innerWidth < 768) {
            // Mobile: shift map up by adding bottom padding to clear the Agent panel and bottom buttons
            mapInstance.fitBounds(bounds, { top: 40, bottom: 180, left: 16, right: 16 });
        } else {
            // Desktop
            mapInstance.fitBounds(bounds, { top: 40, bottom: 40, left: 40, right: 40 });
        }
    }, [selectedCountry]);

    // Re-fit map bounds when selected country changes
    useEffect(() => {
        if (googleMapInstance.current && mapReady) {
            fitVietnamMainland(googleMapInstance.current);
        }
    }, [selectedCountry, mapReady, fitVietnamMainland]);

    // Deleted old computeProjectsBounds declaration from here as it was hoisted above fitVietnamMainland

    const getMapPadding = useCallback(() => {
        if (typeof window !== 'undefined' && window.innerWidth < 768) {
            return { top: 80, bottom: 220, left: 40, right: 40 };
        }
        return { top: 100, bottom: 100, left: 100, right: 100 };
    }, []);

    const handleSelectionChange = useCallback((projects: any[], isActive: boolean) => {
        setSelectedProjects(projects);
        setIsFilterActive(isActive);

        if (googleMapInstance.current) {
            const map = googleMapInstance.current;
            if (!isActive) {
                fitVietnamMainland(map);
            } else if (projects.length === 1) {
                map.panTo({ lat: Number(projects[0].lat), lng: Number(projects[0].lng) });
                map.setZoom(16);
            } else if (projects.length > 1 && (window as any).google?.maps?.LatLngBounds) {
                const bounds = new (window as any).google.maps.LatLngBounds();
                projects.forEach(p => {
                    bounds.extend(new (window as any).google.maps.LatLng(Number(p.lat), Number(p.lng)));
                });
                map.fitBounds(bounds, getMapPadding());

                // Constrain max zoom if points are very close together
                const listener = (window as any).google.maps.event.addListener(map, 'idle', () => {
                    if (map.getZoom() > 15) map.setZoom(15);
                    (window as any).google.maps.event.removeListener(listener);
                });
            }
        }

        if (cesiumRef.current) {
            if (!isActive) {
                cesiumRef.current.resetView();
            } else if (projects.length === 1) {
                cesiumRef.current.flyToCoords(Number(projects[0].lat), Number(projects[0].lng));
            } else if (projects.length > 1) {
                const bbox = computeProjectsBounds(projects);
                if (bbox) cesiumRef.current.fitBoundsLatLng(bbox);
            }
        }
    }, [fitVietnamMainland]);

    const handleProvinceChange = useCallback((provinces: string[]) => {
        if (!featuresWithBBoxes.length) return;

        if (provinces.length === 0) {
            if (googleMapInstance.current) {
                fitVietnamMainland(googleMapInstance.current);
            }
            cesiumRef.current?.resetView();
            return;
        }

        let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
        let valid = false;
        provinces.forEach(provName => {
            const item = featuresWithBBoxes.find(f => f.feature.properties.ten_tinh === provName);
            if (item && item.bbox) {
                if (item.bbox.minLat < minLat) minLat = item.bbox.minLat;
                if (item.bbox.maxLat > maxLat) maxLat = item.bbox.maxLat;
                if (item.bbox.minLng < minLng) minLng = item.bbox.minLng;
                if (item.bbox.maxLng > maxLng) maxLng = item.bbox.maxLng;
                valid = true;
            }
        });

        if (!valid) return;

        if (googleMapInstance.current && (window as any).google?.maps?.LatLngBounds) {
            const map = googleMapInstance.current;
            const bounds = new (window as any).google.maps.LatLngBounds();
            bounds.extend(new (window as any).google.maps.LatLng(minLat, minLng));
            bounds.extend(new (window as any).google.maps.LatLng(maxLat, maxLng));
            map.fitBounds(bounds, getMapPadding());
            const listener = (window as any).google.maps.event.addListener(map, 'idle', () => {
                if (map.getZoom() > 15) map.setZoom(15);
                (window as any).google.maps.event.removeListener(listener);
            });
        }

        cesiumRef.current?.fitBoundsLatLng({ minLat, maxLat, minLng, maxLng });
    }, [featuresWithBBoxes, fitVietnamMainland, getMapPadding]);

    // Automatically fit map bounds when map is ready, geojson features are loaded, and there is an active province or project filter
    useEffect(() => {
        if (!mapReady || !googleMapInstance.current) return;
        
        if (selectedProvinces.length > 0 && featuresWithBBoxes.length > 0) {
            handleProvinceChange(selectedProvinces);
        } else if (isFilterActive && selectedProjects.length > 0) {
            const map = googleMapInstance.current;
            if (selectedProjects.length === 1) {
                map.panTo({ lat: Number(selectedProjects[0].lat), lng: Number(selectedProjects[0].lng) });
                map.setZoom(16);
            } else if ((window as any).google?.maps?.LatLngBounds) {
                const bounds = new (window as any).google.maps.LatLngBounds();
                selectedProjects.forEach(p => {
                    bounds.extend(new (window as any).google.maps.LatLng(Number(p.lat), Number(p.lng)));
                });
                map.fitBounds(bounds, getMapPadding());
            }
        }
    }, [mapReady, selectedProvinces, featuresWithBBoxes, isFilterActive, selectedProjects, handleProvinceChange, getMapPadding]);



    // Handle explicitly clearing search
    const handleClearSearch = useCallback((val: string) => {
        setSearchValue(val);
        if (val.trim()) return;

        if (googleMapInstance.current && mapReady) {
            const map = googleMapInstance.current;
            if (!isFilterActive || selectedProjects.length === 0) {
                fitVietnamMainland(map);
            } else if (selectedProjects.length === 1) {
                map.panTo({ lat: Number(selectedProjects[0].lat), lng: Number(selectedProjects[0].lng) });
                map.setZoom(16);
            } else if ((window as any).google?.maps?.LatLngBounds) {
                const bounds = new (window as any).google.maps.LatLngBounds();
                selectedProjects.forEach(p => bounds.extend(new (window as any).google.maps.LatLng(Number(p.lat), Number(p.lng))));
                map.fitBounds(bounds, getMapPadding());
                map.panToBounds(bounds);
                const listener = (window as any).google.maps.event.addListener(map, 'idle', () => {
                    if (map.getZoom() > 15) map.setZoom(15);
                    (window as any).google.maps.event.removeListener(listener);
                });
            }
        }

        if (cesiumRef.current) {
            if (!isFilterActive || selectedProjects.length === 0) {
                cesiumRef.current.resetView();
            } else if (selectedProjects.length === 1) {
                cesiumRef.current.flyToCoords(Number(selectedProjects[0].lat), Number(selectedProjects[0].lng));
            } else {
                const bbox = computeProjectsBounds(selectedProjects);
                if (bbox) cesiumRef.current.fitBoundsLatLng(bbox);
            }
        }
    }, [selectedProjects, isFilterActive, mapReady, fitVietnamMainland, getMapPadding]);

    // Update marker pins and dynamic clustering
    useEffect(() => {
        if (!mapReady || !googleMapInstance.current || !(window as any).google?.maps) return;

        // Clean up old markers
        markersRef.current.forEach(m => m.setMap(null));
        if (clustererRef.current) {
            clustererRef.current.clearMarkers();
        }

        if (directionsMode) {
            return; // Don't draw markers/clusters when in directions mode
        }

        if (!infoWindowRef.current) {
            infoWindowRef.current = new (window as any).google.maps.InfoWindow();
            infoWindowRef.current.addListener("closeclick", () => {
                activePopupRef.current = null;
                setActiveProject(null);
            });
        }

        const itemsToDisplay = isFilterActive
            ? selectedProjects
            : filteredProjectsByMap;

        const newMarkers = itemsToDisplay.map(item => {
            let markerIcon: any = undefined;

            // Use the same marker icon approach as Admin's googleMapUtils.ts:
            // - scaledSize controls the rendered size on the map
            // - origin (0,0) uses the entire image (no sprite cropping)
            // - anchor at bottom-center for correct pin positioning
            const iconUrl = 'https://sanpham.starglobal3d.vn/map3d/images/icon-vn.gif';
            const markerWidth = 50;
            const markerHeight = 50;

            markerIcon = {
                url: iconUrl,
                size: new (window as any).google.maps.Size(100, 100),
                scaledSize: new (window as any).google.maps.Size(markerWidth, markerHeight),
                origin: new (window as any).google.maps.Point(0, 0),
                anchor: new (window as any).google.maps.Point(markerWidth / 2, markerHeight),
                labelOrigin: new (window as any).google.maps.Point(markerWidth / 2, markerHeight + 2)
            };

            const currentTitle = getProjectDisplayName(item, locale);

            const marker = new (window as any).google.maps.Marker({
                position: { lat: Number(item.lat), lng: Number(item.lng) },
                title: currentTitle,
                icon: markerIcon,
                label: {
                    text: currentTitle,
                    color: '#ffffff',
                    className: 'text-[12px] font-bold text-white whitespace-nowrap [text-shadow:_0_1px_3px_black] block mt-[4px]'
                }
            });

            const contentString = `
                <style>
                    .gm-ui-hover-effect { display: none !important; }
                    .gm-style-iw-c { padding: 0 !important; border-radius: 12px !important; }
                    .gm-style-iw-d { padding: 0 !important; overflow: hidden !important; max-height: none !important; }
                </style>
                <div style="font-family: inherit; width: 280px; text-align: center; background: white; padding: 0 16px 16px 16px; box-sizing: border-box;">
                    <div style="width: 100%; height: 140px; border-radius: 8px; overflow: hidden; background-color: #f3f4f6; position: relative; display: flex; align-items: center; justify-content: center;">
                        <img src="${item.thumbnail || ''}" alt="" style="width: 100%; height: 100%; object-fit: cover;" onerror="this.onerror=null; this.style.display='none'; this.parentElement.innerHTML='<span style=\\'color: #9ca3af; font-size: 13px; font-weight: 500;\\'>${t.noThumbnail}</span>';"/>
                    </div>
                    <div style="margin-top: 14px;">
                        <div style="font-size: 15px; font-weight: 700; margin-bottom: 12px; color: #111; text-transform: uppercase;">
                            ${currentTitle}
                        </div>
                        <div style="display: flex; gap: 8px; width: 100%;">
                            <a href="${getLocalizedProjectUrl(item.url, locale) || '#'}" target="${item.url ? '_blank' : '_self'}" style="flex: 1; display: inline-flex; align-items: center; justify-content: center; background-color: #008a00; color: white; padding: 8px 6px; border-radius: 6px; text-decoration: none; font-weight: 600; font-size: 13px; border: 1px solid #008a00; transition: opacity 0.2s;">
                                ${t.explore360}
                            </a>
                            <button type="button" class="btn-directions" style="flex: 1; display: inline-flex; align-items: center; justify-content: center; background-color: white; color: #008a00; padding: 8px 6px; border-radius: 6px; text-decoration: none; font-weight: 600; font-size: 13px; border: 1px solid #008a00; cursor: pointer; transition: background-color 0.2s;">
                                ${t.getDirections}
                            </button>
                        </div>
                    </div>
                </div>
            `;

            const contentDiv = document.createElement("div");
            contentDiv.innerHTML = sanitizeHtml(contentString);

            contentDiv.addEventListener("mouseenter", () => {
                if (timeoutRef.current) clearTimeout(timeoutRef.current);
            });

            contentDiv.addEventListener("mouseleave", () => {
                timeoutRef.current = setTimeout(() => {
                    infoWindowRef.current.close();
                    activePopupRef.current = null;
                }, 300);
            });

            contentDiv.addEventListener("click", (e) => {
                const btn = (e.target as HTMLElement).closest('.btn-directions');
                if (btn) {
                    e.preventDefault();
                    if (!navigator.geolocation) {
                        alert("Trình duyệt không hỗ trợ định vị (Geolocation).");
                        return;
                    }

                    infoWindowRef.current.close();
                    activePopupRef.current = null;
                    setDirectionsMode(true);
                    setMapViewMode('2d');
                    setMapType('roadmap');
                    activeProjectForDirectionsRef.current = item;
                    setIsUserFocused(true); // Default to focus on user when starting

                    // Start high-accuracy tracking
                    watchIdRef.current = navigator.geolocation.watchPosition(
                        (pos) => {
                            const currentPos = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                            setUserLocation(currentPos);

                            // Update or Create custom user marker
                            if (googleMapInstance.current) {
                                if (!userMarkerRef.current) {
                                    userMarkerRef.current = new (window as any).google.maps.Marker({
                                        position: currentPos,
                                        map: googleMapInstance.current,
                                        icon: {
                                            path: (window as any).google.maps.SymbolPath.CIRCLE,
                                            scale: 8,
                                            fillColor: "#4285F4",
                                            fillOpacity: 1,
                                            strokeColor: "white",
                                            strokeWeight: 2,
                                        },
                                        zIndex: 1000
                                    });
                                } else {
                                    userMarkerRef.current.setPosition(currentPos);
                                }

                                if (isUserFocusedRef.current) {
                                    googleMapInstance.current.panTo(currentPos);
                                    if (googleMapInstance.current.getZoom() < 17) {
                                        googleMapInstance.current.setZoom(18);
                                    }
                                }
                            }

                            // Initial route calculation or update if needed
                            if (!directionsResult) {
                                const destination = { lat: Number(item.lat), lng: Number(item.lng) };

                                if (!directionsServiceRef.current) {
                                    directionsServiceRef.current = new (window as any).google.maps.DirectionsService();
                                }
                                if (!directionsRendererRef.current) {
                                    directionsRendererRef.current = new (window as any).google.maps.DirectionsRenderer({
                                        map: googleMapInstance.current,
                                        suppressMarkers: false,
                                        preserveViewport: true, // We handle viewport manually
                                        polylineOptions: {
                                            strokeColor: "#3b82f6",
                                            strokeOpacity: 0.8,
                                            strokeWeight: 6
                                        }
                                    });
                                }

                                directionsServiceRef.current.route({
                                    origin: currentPos,
                                    destination: destination,
                                    travelMode: (window as any).google.maps.TravelMode.DRIVING
                                }, (response: any, status: any) => {
                                    if (status === "OK") {
                                        directionsRendererRef.current.setMap(googleMapInstance.current);
                                        directionsRendererRef.current.setDirections(response);
                                        setDirectionsResult(response);

                                        if (!isUserFocusedRef.current && response.routes[0]?.bounds) {
                                            googleMapInstance.current.fitBounds(response.routes[0].bounds, 80);
                                        }
                                    }
                                });
                            }
                        },
                        (err) => {
                            console.error("Tracking error:", err);
                        },
                        { enableHighAccuracy: true, maximumAge: 1000, timeout: 5000 }
                    );
                }
            });

            marker.addListener("mouseover", () => {
                if (timeoutRef.current) clearTimeout(timeoutRef.current);

                const uniqueId = String(item.lat) + "_" + String(item.lng);
                if (activePopupRef.current === uniqueId) return;
                activePopupRef.current = uniqueId;

                infoWindowRef.current.setContent(contentDiv);
                infoWindowRef.current.open({
                    anchor: marker,
                    map: googleMapInstance.current,
                    shouldFocus: false
                });
            });

            marker.addListener("mouseout", () => {
                timeoutRef.current = setTimeout(() => {
                    infoWindowRef.current.close();
                    activePopupRef.current = null;
                }, 300);
            });

            marker.addListener("click", () => {
                if (timeoutRef.current) clearTimeout(timeoutRef.current);

                const uniqueId = String(item.lat) + "_" + String(item.lng);
                if (activePopupRef.current === uniqueId) return;
                activePopupRef.current = uniqueId;
                setActiveProject(getProjectNamePath(item));

                infoWindowRef.current.setContent(contentDiv);
                infoWindowRef.current.open({
                    anchor: marker,
                    map: googleMapInstance.current,
                    shouldFocus: false
                });
            });

            return marker;
        });

        if (!clustererRef.current) {
            clustererRef.current = new MarkerClusterer({
                map: googleMapInstance.current,
                markers: newMarkers,
                algorithm: new SuperClusterAlgorithm({ radius: 80, maxZoom: 15 }), // Giảm bán kính cụm và maxZoom để rã cụm sớm hơn khi zoom gần
                onClusterClick: (event, cluster, map) => {
                    if ((window as any).google?.maps?.LatLngBounds) {
                        const bounds = new (window as any).google.maps.LatLngBounds();
                        cluster.markers.forEach((marker: any) => {
                            const pos = marker.getPosition();
                            if (pos) bounds.extend(pos);
                        });
                        map.fitBounds(bounds, getMapPadding());
                        const listener = (window as any).google.maps.event.addListener(map, 'idle', () => {
                            if (map.getZoom() > 18) map.setZoom(18);
                            (window as any).google.maps.event.removeListener(listener);
                        });
                    }
                }
            });
        } else {
            clustererRef.current.addMarkers(newMarkers);
        }

        markersRef.current = newMarkers;

    }, [filteredProjectsByMap, selectedProjects, isFilterActive, mapReady, locale, directionsMode, getMapPadding, setActiveProject]);

    const [pendingDirectionUrl, setPendingDirectionUrl] = useState<string | null>(null);

    const focusProjectOnMap = useCallback((targetItem: any) => {
        if (!googleMapInstance.current || !targetItem) return;

        const targetLat = Number(targetItem.lat);
        const targetLng = Number(targetItem.lng);
        googleMapInstance.current.panTo({ lat: targetLat, lng: targetLng });
        googleMapInstance.current.setZoom(16);
        setActiveProject(getProjectNamePath(targetItem));

        setTimeout(() => {
            const targetMarker = markersRef.current.find(marker => {
                const position = marker.getPosition();
                if (!position) return false;
                return Math.abs(position.lat() - targetLat) < 0.0001 && Math.abs(position.lng() - targetLng) < 0.0001;
            });

            if (targetMarker && infoWindowRef.current) {
                (window as any).google.maps.event.trigger(targetMarker, 'click');
            }
        }, 150);
    }, [setActiveProject]);

    // Automatically focus on the pending project on initial load when map is ready and allProjects is loaded
    useEffect(() => {
        if (!mapReady || !allProjects || allProjects.length === 0 || !activeProject) return;
        if (hasFocusedInitialProjectRef.current) return;

        const cleanUrl = activeProject.replace(/\/+$/, '');
        const targetItem = allProjects.find(p => {
            const pNamePath = (p.name_path || '').replace(/\/+$/, '');
            const pUrl = (p.url || p.title || '').replace(/\/+$/, '');
            return pNamePath === cleanUrl || pUrl === cleanUrl || pUrl.endsWith('/' + cleanUrl) || cleanUrl.endsWith('/' + pUrl);
        });

        if (targetItem) {
            focusProjectOnMap(targetItem);
            hasFocusedInitialProjectRef.current = true;
        }
    }, [mapReady, allProjects, activeProject, focusProjectOnMap]);

    const handleListProjectClick = useCallback((item: any) => {
        if (!item) {
            setActiveProject(null);
            if (infoWindowRef.current) {
                infoWindowRef.current.close();
            }
            activePopupRef.current = null;
            return;
        }

        focusProjectOnMap(item);

        if (mapViewMode === 'cesium') {
            cesiumRef.current?.flyToCoords(Number(item.lat), Number(item.lng));
        }
    }, [focusProjectOnMap, mapViewMode, setActiveProject]);

    const searchResults = useMemo(() => {
        if (!searchValue.trim() || filteredProjectsByMap.length === 0) return [];
        const lowerSearch = searchValue.toLowerCase();

        const matchedFieldKeys = Object.entries(t.fields)
            .filter(([k, val]) => val.toLowerCase().includes(lowerSearch) || k.toLowerCase().includes(lowerSearch))
            .map(([k]) => k.toLowerCase());

        return filteredProjectsByMap.filter(p => {
            const dispVn = (p.display_vn || "").toLowerCase();
            const dispEn = (p.display_en || "").toLowerCase();
            const dispJa = (p.display_ja || "").toLowerCase();
            const title = (p.title || p.db_name || "").toLowerCase();
            const field = (p.field || "").toLowerCase();

            if (matchedFieldKeys.some(key => field.includes(key))) return true;
            if (dispVn.includes(lowerSearch) || dispEn.includes(lowerSearch) || dispJa.includes(lowerSearch) || title.includes(lowerSearch)) return true;
            return false;
        }).map(p => ({
            ...p,
            displayTitle: getProjectDisplayName(p, locale)
        }));
    }, [searchValue, filteredProjectsByMap, t.fields, locale]);

    const renderSearchDropdown = () => {
        if (!isSearchOpen || !searchValue.trim() || searchResults.length === 0) return null;
        return (
            <div className="absolute top-[110%] left-0 w-full min-w-[320px] md:w-[420px] bg-[#1a21338c] backdrop-blur-[6px] rounded-[16px] shadow-2xl border border-white/10 text-white overflow-hidden z-50">
                <div className="p-4">
                    <h4 className="text-[14px] font-bold tracking-widest text-white/90 mb-3 px-2 uppercase shadow-sm">
                        {searchResults.length < 10 ? `0${searchResults.length}` : searchResults.length} KẾT QUẢ:
                    </h4>
                    <div className="flex flex-col gap-2 max-h-[50vh] overflow-y-auto pr-1 [&::-webkit-scrollbar]:w-[6px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-white/20 [&::-webkit-scrollbar-thumb]:rounded-[6px]">
                        {searchResults.map((item, idx) => (
                            <button
                                key={idx}
                                onClick={() => {
                                    setIsSearchOpen(false);
                                    focusProjectOnMap(item);
                                    if (mapViewMode === 'cesium') {
                                        cesiumRef.current?.flyToCoords(Number(item.lat), Number(item.lng));
                                    }
                                }}
                                className="flex items-center gap-4 py-2.5 px-2 hover:bg-white/10 transition-colors rounded-xl overflow-hidden w-full text-left group shrink-0"
                            >
                                <div className="w-[120px] h-[72px] shrink-0 rounded-xl overflow-hidden border-2 border-white/80 group-hover:border-yellow-400 transition-colors">
                                    <img src={item.thumbnail || ''} className="object-cover w-full h-full" onError={(e) => { e.currentTarget.style.display = 'none'; }} alt="" />
                                </div>
                                <span className="text-[14px] font-bold text-white uppercase line-clamp-3 leading-snug group-hover:text-yellow-400 transition-colors">
                                    {item.displayTitle}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    // Lắng nghe sự kiện từ AI Agent để mở hướng dẫn chỉ đường
    useEffect(() => {
        const handleOpenMapDirections = (e: any) => {
            const projectUrl = e.detail?.project_url;
            if (projectUrl) {
                setPendingDirectionUrl(projectUrl);
            }
        };
        window.addEventListener('open_map_directions', handleOpenMapDirections);
        return () => window.removeEventListener('open_map_directions', handleOpenMapDirections);
    }, []);

    useEffect(() => {
        const handleFocusMapProject = (e: any) => {
            const projectUrl = e.detail?.project_url;
            if (!projectUrl || allProjects.length === 0 || !mapReady) return;

            const cleanUrl = projectUrl.replace(/\/+$/, '');
            const targetItem = allProjects.find(p => p.url && p.url.replace(/\/+$/, '') === cleanUrl);
            if (targetItem) {
                focusProjectOnMap(targetItem);
            }
        };

        window.addEventListener('focus_map_project', handleFocusMapProject);
        return () => window.removeEventListener('focus_map_project', handleFocusMapProject);
    }, [allProjects, focusProjectOnMap, mapReady]);

    useEffect(() => {
        const handleSwitchProvinceMode = (e: any) => {
            const mode = e.detail?.mode;
            if (mode === '34' || mode === '63') {
                setProvinceMode(mode);
                setSelectedProvinces([]);
                setSelectedProjectUrls([]);
                setSelectedFields([]);
            }
        };
        window.addEventListener('switch_province_mode', handleSwitchProvinceMode);
        return () => window.removeEventListener('switch_province_mode', handleSwitchProvinceMode);
    }, [setSelectedProvinces, setSelectedProjectUrls, setSelectedFields]);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('global_map_filter_state', { detail: { isOpen: isFilterOpen } }));
        }
    }, [isFilterOpen]);

    // Thực thi chỉ đường khi có pending request và dữ liệu map đã sẵn sàng
    useEffect(() => {
        if (pendingDirectionUrl && allProjects.length > 0 && mapReady && googleMapInstance.current) {
            const cleanUrl = pendingDirectionUrl.replace(/\/+$/, '');
            const targetItem = allProjects.find(p => p.url && p.url.replace(/\/+$/, '') === cleanUrl);

            // Xóa pending khỏi state để không chạy lại
            setPendingDirectionUrl(null);

            if (targetItem) {
                focusProjectOnMap(targetItem);

                if (!navigator.geolocation) {
                    alert("Trình duyệt không hỗ trợ định vị (Geolocation).");
                    return;
                }
                if (infoWindowRef.current) {
                    infoWindowRef.current.close();
                }
                activePopupRef.current = null;

                // Thêm delay nhỏ để chắc chắn map đã sẵn sàng mở
                setTimeout(() => {
                    setDirectionsMode(true);
                    setMapType('roadmap');
                    activeProjectForDirectionsRef.current = targetItem;
                    setIsUserFocused(true);

                    if (watchIdRef.current) {
                        navigator.geolocation.clearWatch(watchIdRef.current);
                    }
                    watchIdRef.current = navigator.geolocation.watchPosition(
                        (pos) => {
                            const currentPos = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                            setUserLocation(currentPos);

                            if (googleMapInstance.current) {
                                if (!userMarkerRef.current) {
                                    userMarkerRef.current = new (window as any).google.maps.Marker({
                                        position: currentPos,
                                        map: googleMapInstance.current,
                                        icon: {
                                            path: (window as any).google.maps.SymbolPath.CIRCLE,
                                            scale: 8,
                                            fillColor: "#4285F4",
                                            fillOpacity: 1,
                                            strokeColor: "white",
                                            strokeWeight: 2,
                                        },
                                        zIndex: 1000
                                    });
                                } else {
                                    userMarkerRef.current.setPosition(currentPos);
                                }

                                if (isUserFocusedRef.current) {
                                    googleMapInstance.current.panTo(currentPos);
                                    if (googleMapInstance.current.getZoom() < 17) {
                                        googleMapInstance.current.setZoom(18);
                                    }
                                }
                            }

                            if (!directionsResult) {
                                const destination = { lat: Number(targetItem.lat), lng: Number(targetItem.lng) };

                                if (!directionsServiceRef.current) {
                                    directionsServiceRef.current = new (window as any).google.maps.DirectionsService();
                                }
                                if (!directionsRendererRef.current) {
                                    directionsRendererRef.current = new (window as any).google.maps.DirectionsRenderer({
                                        map: googleMapInstance.current,
                                        suppressMarkers: false,
                                        preserveViewport: true,
                                        polylineOptions: { strokeColor: "#3b82f6", strokeOpacity: 0.8, strokeWeight: 6 }
                                    });
                                }

                                directionsServiceRef.current.route({
                                    origin: currentPos,
                                    destination: destination,
                                    travelMode: (window as any).google.maps.TravelMode.DRIVING
                                }, (response: any, status: any) => {
                                    if (status === "OK") {
                                        directionsRendererRef.current.setMap(googleMapInstance.current);
                                        directionsRendererRef.current.setDirections(response);
                                        setDirectionsResult(response);

                                        if (!isUserFocusedRef.current && response.routes[0]?.bounds) {
                                            googleMapInstance.current.fitBounds(response.routes[0].bounds, 80);
                                        }
                                    }
                                });
                            }
                        },
                        (err) => console.error("Tracking error:", err),
                        { enableHighAccuracy: true, maximumAge: 1000, timeout: 5000 }
                    );
                }, 500);
            }
        }
    }, [pendingDirectionUrl, allProjects, mapReady, directionsResult, focusProjectOnMap]);


    // Fetch all projects when map opens
    useEffect(() => {
        if (!isOpen) return;
        const fetchAll = async () => {
            try {
                const { data } = await projectService.getProjectsByLocation({
                    limit: 1000,
                    active: 1
                });
                if (data && Array.isArray(data)) {
                    setAllProjects(data.filter((p: any) => p.lat && p.lng));
                }
            } catch (error) {
                console.error("Failed to fetch all projects", error);
            }
        };
        fetchAll();
    }, [isOpen]);

    // Re-trigger directions when locale changes
    useEffect(() => {
        if (directionsMode && activeProjectForDirectionsRef.current && userLocation) {
            // Re-fetch route to get instructions in new language
            const destination = {
                lat: Number(activeProjectForDirectionsRef.current.lat),
                lng: Number(activeProjectForDirectionsRef.current.lng)
            };

            directionsServiceRef.current?.route({
                origin: userLocation,
                destination: destination,
                travelMode: (window as any).google.maps.TravelMode.DRIVING
            }, (response: any, status: any) => {
                if (status === "OK") {
                    setDirectionsResult(response);
                    directionsRendererRef.current?.setDirections(response);
                }
            });
        }
    }, [locale]);

    // Fetch API key & init map
    useEffect(() => {
        if (!isOpen) return;

        let isMounted = true;

        const initMapInstance = () => {
            if (!mapRef.current || !isMounted) return;
            googleMapInstance.current = new (window as any).google.maps.Map(mapRef.current, {
                center: { lat: 16.0583, lng: 108.2772 },
                zoom: 6,
                mapTypeId: mapType,
                disableDefaultUI: true,
                gestureHandling: "greedy",
                styles: [
                    {
                        featureType: "administrative.country",
                        elementType: "geometry.stroke",
                        stylers: [{ visibility: "off" }]
                    },
                    {
                        featureType: "administrative.province",
                        elementType: "geometry.stroke",
                        stylers: [{ visibility: "off" }]
                    }
                ]
            });

            const closePopup = () => {
                if (infoWindowRef.current) {
                    infoWindowRef.current.close();
                }
                activePopupRef.current = null;
                setActiveProject(null);
            };

            googleMapInstance.current.addListener("click", closePopup);
            googleMapInstance.current.addListener("dragstart", closePopup);

            googleMapInstance.current.data.setStyle({
                fillColor: 'transparent',
                strokeColor: '#ffffffff',
                strokeWeight: 1.5,
                fillOpacity: 0,
                clickable: false
            });

            // Center Vietnam map based on viewport
            fitVietnamMainland(googleMapInstance.current);

            setMapReady(true);
        };

        if ((window as any).google?.maps) {
            initMapInstance();
        } else {
            const initMap = async () => {
                try {
                    const base = '/api/proxy';

                    const lang = locale === 'en' ? 'en' : 'vi';
                    const scriptId = 'google-maps-script';

                    let script = document.getElementById(scriptId) as HTMLScriptElement;
                    if (!script) {
                        script = document.createElement('script');
                        script.id = scriptId;
                        script.src = `${base}/v1/general/services/maps-js?language=${lang}`;
                        script.async = true;
                        script.defer = true;

                        script.onload = () => {
                            if (isMounted) initMapInstance();
                        };
                        document.head.appendChild(script);
                    } else {
                        const existingOnload = script.onload;
                        script.onload = (e) => {
                            if (existingOnload) (existingOnload as any)(e);
                            if (isMounted) initMapInstance();
                        };
                    }
                } catch (err) {
                    console.error("Failed to load map API key", err);
                }
            };
            initMap();
        }

        return () => {
            isMounted = false;
            googleMapInstance.current = null;
            if (clustererRef.current) {
                clustererRef.current.clearMarkers();
                clustererRef.current = null;
            }
            markersRef.current = [];
            setMapReady(false);
        };
    }, [isOpen, fitVietnamMainland]); // Do not re-run when locale changes to prevent map breakage

    // Sync Google Maps data layer with active province mode and loaded GeoJSON
    useEffect(() => {
        if (!mapReady || !googleMapInstance.current) return;
        const activeGeoJson = provinceMode === '34' ? rawGeoJson34 : rawGeoJson63;

        // Only update map features if we have the active GeoJSON loaded
        if (!activeGeoJson) return;

        // Clear existing features
        googleMapInstance.current.data.forEach((feature: any) => {
            googleMapInstance.current.data.remove(feature);
        });

        // Add new features
        googleMapInstance.current.data.addGeoJson(activeGeoJson);

        // Apply dynamic stroke color based on province mode
        googleMapInstance.current.data.setStyle({
            fillColor: 'transparent',
            strokeColor: provinceMode === '34' ? '#FFF500' : '#ffffff',
            strokeWeight: 1.5,
            fillOpacity: 0,
            clickable: false
        });

        // Fit Vietnam mainland bounds when provinceMode changes, if no filter is active
        if (!isFilterActive || selectedProjects.length === 0) {
            fitVietnamMainland(googleMapInstance.current);
        }
    }, [mapReady, provinceMode, rawGeoJson34, rawGeoJson63, isFilterActive, selectedProjects, fitVietnamMainland]);

    // Handle map type changes
    useEffect(() => {
        if (googleMapInstance.current && (window as any).google?.maps) {
            const actualType = directionsMode ? 'roadmap' : mapType;
            googleMapInstance.current.setMapTypeId(actualType);
        }
    }, [mapType, directionsMode]);

    // Zoom handlers
    const handleZoomIn = () => {
        if (googleMapInstance.current) {
            const currentZoom = googleMapInstance.current.getZoom();
            googleMapInstance.current.setZoom(currentZoom + 1);
        }
    };

    const handleZoomOut = () => {
        if (googleMapInstance.current) {
            const currentZoom = googleMapInstance.current.getZoom();
            googleMapInstance.current.setZoom(currentZoom - 1);
        }
    };

    // Prevent scroll on body when map is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [isOpen]);

    // Close on Escape key
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        if (isOpen) {
            window.addEventListener("keydown", handleKeyDown);
        }
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, onClose]);

    // Reset all mapping overlay memory contexts firmly when gracefully closed 
    useEffect(() => {
        if (!isOpen) {
            setSearchValue("");
            setSelectedProjects([]);
            setIsFilterActive(false);
            setIsSearchOpen(false);
            setIsFilterOpen(false);
            setSelectedCountry('all');
            setMapType('satellite'); // reset to satellite on close
            setMapViewMode('2d');
            setDirectionsMode(false);
            if (directionsRendererRef.current) {
                directionsRendererRef.current.setMap(null);
            }
        }
    }, [isOpen]);

    const handleExitDirections = useCallback(() => {
        setDirectionsMode(false);
        setDirectionsResult(null);
        setIsUserFocused(false);
        setMapType('satellite');

        if (watchIdRef.current !== null) {
            navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
        }

        if (userMarkerRef.current) {
            userMarkerRef.current.setMap(null);
            userMarkerRef.current = null;
        }

        if (directionsRendererRef.current) {
            directionsRendererRef.current.setMap(null);
            directionsRendererRef.current.setDirections({ routes: [] });
        }

        if (activeProjectForDirectionsRef.current && googleMapInstance.current) {
            googleMapInstance.current.panTo({
                lat: Number(activeProjectForDirectionsRef.current.lat),
                lng: Number(activeProjectForDirectionsRef.current.lng)
            });
            googleMapInstance.current.setZoom(15);
        }
        activeProjectForDirectionsRef.current = null;
    }, []);

    useEffect(() => {
        const handleResetFilterEvent = () => {
            setSearchValue("");
            setIsSearchOpen(false);
            setIsFilterOpen(false);
            handleExitDirections();
            if (googleMapInstance.current) {
                fitVietnamMainland(googleMapInstance.current);
            }
            cesiumRef.current?.resetView();
        };
        window.addEventListener('reset_map_filter', handleResetFilterEvent);
        return () => window.removeEventListener('reset_map_filter', handleResetFilterEvent);
    }, [handleExitDirections, fitVietnamMainland]);

    useEffect(() => {
        const handleCloseDirectionsEvent = () => {
            handleExitDirections();
        };
        window.addEventListener('close_map_directions', handleCloseDirectionsEvent);
        return () => window.removeEventListener('close_map_directions', handleCloseDirectionsEvent);
    }, [handleExitDirections]);

    const steps = useMemo(() => {
        if (!directionsResult?.routes?.[0]?.legs?.[0]?.steps) return [];
        return directionsResult.routes[0].legs[0].steps;
    }, [directionsResult]);

    const totalDistance = useMemo(() => {
        return directionsResult?.routes?.[0]?.legs?.[0]?.distance?.text || "";
    }, [directionsResult]);

    const totalDuration = useMemo(() => {
        return directionsResult?.routes?.[0]?.legs?.[0]?.duration?.text || "";
    }, [directionsResult]);

    const globeProjects = useMemo(() => {
        return isFilterActive ? selectedProjects : filteredProjectsByMap;
    }, [filteredProjectsByMap, isFilterActive, selectedProjects]);

    if (!isOpen) return null;

    return createPortal(
        <>
            {/* Map container */}
            <div
                className="fixed inset-0 z-[110]"
                style={{ top: "64px" }}
            >
                <div
                    ref={mapRef}
                    className="w-full h-full bg-[#1e293b]"
                />
            </div>
            {mapViewMode === 'cesium' && (
                <div className="fixed inset-0 top-[64px] z-[120] overflow-hidden bg-[#020617]">
                    <CesiumGlobeView
                        ref={cesiumRef}
                        locale={locale}
                        projects={globeProjects}
                        provinceMode={provinceMode}
                        provinceGeoJson={
                            selectedCountry === 'vietnam'
                                ? (provinceMode === '34' ? rawGeoJson34 : rawGeoJson63)
                                : selectedCountry === 'usa'
                                ? rawGeoJsonUSA
                                : selectedCountry === 'canada'
                                ? rawGeoJsonCAN
                                : rawGeoJsonAUS
                        }
                        selectedCountry={selectedCountry}
                    />
                </div>
            )}

            {/* ── FLOATING BUTTONS — Desktop ── */}
            {/* Close: top-left alone */}
            {!directionsMode && (
                <button
                    onClick={onClose}
                    className="hidden md:flex fixed top-[80px] left-4 z-[130] items-center justify-center text-white/80 hover:text-white bg-[#00000080] hover:bg-[#000000aa] backdrop-blur-[20px] transition-all p-3 rounded-full shadow-lg"
                    title={t.closeMap}
                >
                    <CloseIcon />
                </button>
            )}

            <div ref={desktopControlsRef} className={`hidden md:flex flex-col items-end fixed top-[80px] right-4 z-[130] gap-2 ${directionsMode ? 'opacity-0 pointer-events-none' : ''}`}>
                <div className="flex items-center gap-2">

                    {/* Search bar */}
                    <div className="relative flex items-center gap-2 w-80 lg:w-96 bg-[#00000080] hover:bg-[#000000aa] backdrop-blur-[20px] transition-colors rounded-full px-4 py-3 cursor-text shadow-lg">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor" className="size-4 text-white shrink-0">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 15.803a7.5 7.5 0 0 0 10.607 0Z" />
                        </svg>
                        <input
                            type="text"
                            value={searchValue}
                            onChange={(e) => {
                                handleClearSearch(e.target.value);
                                setIsSearchOpen(true);
                            }}
                            onFocus={() => {
                                setIsSearchOpen(true);
                                setIsFilterOpen(false);
                            }}
                            placeholder={t.searchPlaceholder}
                            className="flex-1 bg-transparent text-white text-sm placeholder-white/70 outline-none min-w-0"
                        />
                        {searchValue && (
                            <button onClick={() => handleClearSearch("")} className="text-white/50 hover:text-white transition-colors">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="size-4">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                        {renderSearchDropdown()}
                    </div>

                    {/* Bộ lọc dự án */}
                    <button
                        onClick={() => {
                            setIsFilterOpen(!isFilterOpen);
                            setIsSearchOpen(false);
                        }}
                        className={`flex items-center gap-2 transition-all px-4 py-3 rounded-full text-sm font-medium whitespace-nowrap shadow-lg ${isFilterOpen ? 'bg-white/30 text-white backdrop-blur-[20px]' : 'text-white bg-[#00000080] hover:bg-[#000000aa] backdrop-blur-[20px]'}`}>
                        <FilterIcon />
                        <span>{t.filterProjectBtn}</span>
                    </button>
                    {isCesiumActive && (
                        <button
                            onClick={() => setMapViewMode(mapViewMode === 'cesium' ? '2d' : 'cesium')}
                            className={`flex h-11 w-11 items-center justify-center rounded-full text-white shadow-lg transition-all backdrop-blur-[20px] ${mapViewMode === 'cesium' ? 'bg-white/30' : 'bg-[#00000080] hover:bg-[#000000aa]'}`}
                            title={mapViewMode === 'cesium' ? (locale === 'en' ? 'Google Map' : 'Bản đồ') : 'Cesium Globe'}
                            aria-label={mapViewMode === 'cesium' ? (locale === 'en' ? 'Google Map' : 'Bản đồ') : 'Cesium Globe'}
                        >
                            {mapViewMode === 'cesium' ? <MapIcon className="size-5" /> : <Earth className="size-5" />}
                        </button>
                    )}
                </div>

                {/* Popups - Desktop */}
                <div className="mt-2">
                    <div className={isFilterOpen ? "block" : "hidden"}>
                        <ListPopup
                            locale={locale}
                            allProjects={filteredProjectsByMap}
                            onSelectionChange={handleSelectionChange}
                            onProjectClick={handleListProjectClick}
                            mapReady={mapReady}
                            isOpen={isFilterOpen}
                            onCloseFilter={() => setIsFilterOpen(false)}
                            featuresWithBBoxes={featuresWithBBoxes}
                            onProvinceChange={handleProvinceChange}
                            provinceMode={provinceMode}
                            selectedCountry={selectedCountry}
                            onCountryChange={setSelectedCountry}
                            selectedProvinces={selectedProvinces}
                            setSelectedProvinces={setSelectedProvinces}
                            selectedFields={selectedFields}
                            setSelectedFields={setSelectedFields}
                            selectedProjectUrls={selectedProjectUrls}
                            setSelectedProjectUrls={setSelectedProjectUrls}
                        />
                    </div>
                </div>
            </div>

            {/* ── FLOATING BUTTONS — Mobile ── */}
            <div ref={mobileControlsRef} className={`md:hidden fixed top-[80px] left-4 right-4 z-[130] flex flex-col gap-2 ${directionsMode ? 'opacity-0 pointer-events-none' : ''}`}>
                {/* Row 1: Province mode toggle + Search bar */}
                <div className="flex items-center gap-2">

                    {/* Search bar */}
                    <div className="flex-1 relative flex items-center gap-2 bg-[#00000080] backdrop-blur-[20px] rounded-full px-4 py-3 shadow-lg cursor-text min-w-0">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor" className="size-4 text-white shrink-0">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 15.803a7.5 7.5 0 0 0 10.607 0Z" />
                        </svg>
                        <input
                            type="text"
                            value={searchValue}
                            onChange={(e) => {
                                handleClearSearch(e.target.value);
                                setIsSearchOpen(true);
                            }}
                            onFocus={() => {
                                setIsSearchOpen(true);
                                setIsFilterOpen(false);
                            }}
                            placeholder={t.searchPlaceholder}
                            className="flex-1 bg-transparent text-white text-sm placeholder-white/70 outline-none min-w-0"
                        />
                        {searchValue && (
                            <button onClick={() => handleClearSearch("")} className="text-white/50 hover:text-white transition-colors">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="size-4">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                        {renderSearchDropdown()}
                    </div>
                </div>

                {/* Row 2: Close (left) + spacer + Bộ lọc dự án (right) */}
                <div className="flex items-center gap-2">
                    <button
                        onClick={onClose}
                        className="flex items-center justify-center text-white bg-[#00000080] backdrop-blur-[20px] transition-all p-3 rounded-full shadow-lg shrink-0"
                        title={t.closeMap}
                    >
                        <CloseIcon />
                    </button>
                    <div className="flex-1" />
                    <button
                        onClick={() => {
                            setIsFilterOpen(!isFilterOpen);
                            setIsSearchOpen(false);
                        }}
                        className={`flex items-center gap-2 transition-all px-4 py-3 rounded-full text-sm font-medium shadow-lg ${isFilterOpen ? 'bg-white/30 text-white backdrop-blur-[20px]' : 'text-white bg-[#00000080] backdrop-blur-[20px]'}`}>
                        <FilterIcon />
                        <span>{t.filterProjectBtn}</span>
                    </button>
                    {isCesiumActive && (
                        <button
                            onClick={() => setMapViewMode(mapViewMode === 'cesium' ? '2d' : 'cesium')}
                            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-white shadow-lg transition-all backdrop-blur-[20px] ${mapViewMode === 'cesium' ? 'bg-white/30' : 'bg-[#00000080]'}`}
                            title={mapViewMode === 'cesium' ? (locale === 'en' ? 'Google Map' : 'Bản đồ') : 'Cesium Globe'}
                            aria-label={mapViewMode === 'cesium' ? (locale === 'en' ? 'Google Map' : 'Bản đồ') : 'Cesium Globe'}
                        >
                            {mapViewMode === 'cesium' ? <MapIcon className="size-5" /> : <Earth className="size-5" />}
                        </button>
                    )}
                </div>

                {/* Popups - Mobile */}
                <div className="mt-2 w-full flex justify-center">
                    <div className={isFilterOpen ? "block" : "hidden"}>
                        <ListPopup
                            locale={locale}
                            allProjects={filteredProjectsByMap}
                            onSelectionChange={handleSelectionChange}
                            onProjectClick={handleListProjectClick}
                            mapReady={mapReady}
                            isOpen={isFilterOpen}
                            onCloseFilter={() => setIsFilterOpen(false)}
                            featuresWithBBoxes={featuresWithBBoxes}
                            onProvinceChange={handleProvinceChange}
                            provinceMode={provinceMode}
                            selectedCountry={selectedCountry}
                            onCountryChange={setSelectedCountry}
                            selectedProvinces={selectedProvinces}
                            setSelectedProvinces={setSelectedProvinces}
                            selectedFields={selectedFields}
                            setSelectedFields={setSelectedFields}
                            selectedProjectUrls={selectedProjectUrls}
                            setSelectedProjectUrls={setSelectedProjectUrls}
                        />
                    </div>
                </div>
            </div>

            {/* ── BOTTOM-LEFT LOGO & CONTROLS ── */}
            {!directionsMode && (
                <div
                    className="fixed z-[130] bg-white rounded-full flex items-center justify-center border border-gray-200 pointer-events-auto px-3"
                    style={{
                        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.15)',
                        height: '32px',
                        width: '150px',
                        backgroundColor: '#ffffff',
                        bottom: '2px',
                        left: '2px',
                    }}
                >
                    <img
                        src="/images/H_Logo_Light.png"
                        alt="Logo"
                        style={{
                            height: '24px',
                            width: 'auto',
                            objectFit: 'contain',
                            imageRendering: '-webkit-optimize-contrast',
                        }}
                    />
                </div>
            )}

            <div className={`fixed bottom-[42px] left-4 z-[130] flex flex-col items-start gap-2 pointer-events-auto ${directionsMode ? 'hidden' : ''}`}>
                {/* Zoom In */}
                <button
                    onClick={handleZoomIn}
                    className="flex items-center justify-center w-11 h-11 bg-[#00000080] backdrop-blur-[20px] hover:bg-[#000000aa] transition-all rounded-[16px] shadow-lg text-white"
                    title={t.zoomIn}
                >
                    <svg width="29" height="29" viewBox="0 0 29 29" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-5 h-5">
                        <path d="M14.5 1.5L14.5 27.5" stroke="currentColor" strokeWidth="3" strokeLinecap="square" />
                        <path d="M27.5 14.5L1.5 14.5" stroke="currentColor" strokeWidth="3" strokeLinecap="square" />
                    </svg>
                </button>

                {/* Zoom Out */}
                <button
                    onClick={handleZoomOut}
                    className="flex items-center justify-center w-11 h-11 bg-[#00000080] backdrop-blur-[20px] hover:bg-[#000000aa] transition-all rounded-[16px] shadow-lg text-white"
                    title={t.zoomOut}
                >
                    <svg width="29" height="3" viewBox="0 0 29 3" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-5 h-0.5">
                        <path d="M1.5 1.5H27.5" stroke="currentColor" strokeWidth="3" strokeLinecap="square" />
                    </svg>
                </button>

                {/* Map type pill toggle */}
                <div
                    className="flex items-stretch rounded-full overflow-hidden"
                    style={{ boxShadow: '0 2px 8px #00000040' }}
                >
                    <button
                        onClick={() => setMapType('satellite')}
                        className="text-[11px] font-semibold whitespace-nowrap px-4 py-2 transition-all text-white"
                        style={{
                            backdropFilter: 'blur(12px)',
                            background: mapType === 'satellite' ? 'rgba(255, 255, 255, 0.2)' : '#00000080',
                        }}
                    >
                        {t.satellite}
                    </button>
                    <button
                        onClick={() => setMapType('terrain')}
                        className="text-[11px] font-semibold whitespace-nowrap px-4 py-2 transition-all text-white"
                        style={{
                            backdropFilter: 'blur(12px)',
                            background: mapType === 'terrain' ? 'rgba(255, 255, 255, 0.2)' : '#00000080',
                        }}
                    >
                        {t.terrain}
                    </button>
                </div>
            </div>

            {/* Directions Mode Overlay */}
            {directionsMode && (
                <div className="fixed top-[80px] left-1/2 -translate-x-1/2 z-[130] flex flex-col items-center gap-1.5 w-[calc(100%-32px)] md:w-auto">
                    {/* Main Consolidated Navigation Bar */}
                    <div className="bg-[#1a213399] backdrop-blur-[10px] text-white pl-5 pr-3 py-2 rounded-full shadow-2xl border border-white/10 flex items-center gap-3 w-full md:w-auto min-h-[56px]">
                        {/* Part 1: Overall Stats */}
                        <div className="flex flex-col shrink-0">
                            <div className="flex items-center gap-1.5">
                                <span className="text-yellow-400 font-bold text-sm leading-none">{totalDistance}</span>
                                <span className="text-white/40 text-[9px] uppercase font-bold hidden sm:inline">{t.totalDistanceLabel}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <span className="text-white font-bold text-sm leading-none">{totalDuration}</span>
                                <span className="text-white/40 text-[9px] uppercase font-bold hidden sm:inline">{t.totalDurationLabel}</span>
                            </div>
                        </div>

                        <div className="h-8 w-px bg-white/10 shrink-0" />

                        {/* Part 2: Current Instruction (Merged Step) */}
                        {steps.length > 0 && (
                            <div
                                className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer hover:bg-white/5 transition-colors px-2 py-1 rounded-xl"
                                onClick={() => setIsStepsExpanded(!isStepsExpanded)}
                            >
                                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center text-blue-400 shrink-0 shadow-inner">
                                    <ManeuverIcon maneuver={steps[0].maneuver} size="size-5" />
                                </div>
                                <div className="flex flex-col min-w-0">
                                    <div
                                        className="text-[13px] text-white font-bold leading-tight line-clamp-1 [&>b]:text-yellow-400"
                                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(steps[0].instructions) }}
                                    />
                                    <span className="text-white/40 text-[10px] font-bold uppercase tracking-tight">{steps[0].distance.text}</span>
                                </div>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className={`size-3 text-white/20 transition-transform shrink-0 ${isStepsExpanded ? 'rotate-180' : ''}`}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                                </svg>
                            </div>
                        )}

                        <div className="h-8 w-px bg-white/10 shrink-0" />

                        {/* Part 3: Location Toggle */}
                        <button
                            onClick={() => {
                                const newFocus = !isUserFocused;
                                setIsUserFocused(newFocus);
                                if (newFocus && userLocation && googleMapInstance.current) {
                                    googleMapInstance.current.panTo(userLocation);
                                    googleMapInstance.current.setZoom(18);
                                } else if (!newFocus && directionsResult?.routes?.[0]?.bounds && googleMapInstance.current) {
                                    googleMapInstance.current.fitBounds(directionsResult.routes[0].bounds, 80);
                                }
                            }}
                            className={`flex items-center gap-1.5 transition-all px-3 py-2 rounded-full border text-[10px] font-extrabold uppercase tracking-tighter shrink-0 ${isUserFocused ? 'bg-blue-500/40 border-blue-400 text-white' : 'hover:bg-white/10 border-white/10 text-white/60'}`}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="size-3.5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                            </svg>
                            <span className="hidden sm:inline">{isUserFocused ? t.routeView : t.currentLocation}</span>
                        </button>

                        {/* Part 4: Exit */}
                        <button
                            onClick={handleExitDirections}
                            className="w-10 h-10 flex items-center justify-center text-white/40 hover:text-red-400 hover:bg-red-400/10 rounded-full transition-all shrink-0"
                        >
                            <CloseIcon />
                        </button>
                    </div>

                    {/* Dropdown Steps List - Positioned relative to the main bar */}
                    {directionsResult && steps.length > 0 && isStepsExpanded && (
                        <div className="w-full max-w-[450px] animate-in fade-in slide-in-from-top-2 duration-200">
                            <div className="bg-[#1a2133b3] backdrop-blur-[12px] rounded-[24px] shadow-2xl border border-white/10 overflow-hidden flex flex-col">
                                <div className="max-h-[35vh] overflow-y-auto px-1 pb-2 [&::-webkit-scrollbar]:w-[3px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-white/10 [&::-webkit-scrollbar-thumb]:rounded-[4px]">
                                    <div className="pt-3 px-4 pb-1 text-[9px] font-black text-white/20 uppercase tracking-[2px]">{t.stepsTitle}</div>
                                    {steps.slice(1).map((step: any, index: number) => (
                                        <div key={index} className="flex gap-3 p-3 items-start group hover:bg-white/5 transition-colors rounded-2xl mx-1">
                                            <div className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center text-white/30 group-hover:bg-white/10 group-hover:text-blue-400 transition-all shrink-0">
                                                <ManeuverIcon maneuver={step.maneuver} size="size-4" />
                                            </div>
                                            <div className="flex flex-col gap-0.5 min-w-0 pt-0.5">
                                                <div
                                                    className="text-[12px] text-white/70 leading-snug [&>b]:text-yellow-400"
                                                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(step.instructions) }}
                                                />
                                                <span className="text-white/30 text-[10px] font-medium">{step.distance.text}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>,
        document.body
    );
}
